import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CategoryBlocks, ViewSwitch, MetalKpi } from "@/components/foodcost/FoodCostUI";
import { ConsumptionItemCards } from "@/components/foodcost/ItemCards";
import { ShoppingBasket, Boxes, Coins, Users, ChefHat, ConciergeBell, UtensilsCrossed, Target, ReceiptText } from "lucide-react";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Utensils, Sparkles, Save, Download, Upload, Plus, Edit, Trash2, FileSpreadsheet,
  Loader2, TrendingUp, TrendingDown, FileText, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useCurrency";
import { formatMoney, resolveCurrency } from "@/utils/money";
import PurchasePlannerTab from "@/pages/PurchasePlannerTab";
import { PreparedFoodWastageTab } from "@/pages/PreparedFoodWastage";

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };
const firstOfMonth = () => today().slice(0, 8) + "01";

export default function ConsumptionFoodCost() {
  const { session } = useAuth();
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const cur = useCurrency(center);
  const isEditor = session?.is_super_admin || session?.is_admin || session?.is_accounts;
  // Grid editor = anyone who can see the Consumption page (Sales & Cash / Operations
  // role, or Admin / Accounts / Super Admin). Center Managers must be able to
  // enter qty (Received / Staff / Guest / Wastage / Remarks) — page-level sidebar
  // visibility already gates access.
  const roleMap = session?.roles || {};
  const isGridEditor = Boolean(
    session && (
      session.is_super_admin || session.is_admin || session.is_accounts ||
      roleMap.sales_cash === true || roleMap.operations === true
    )
  );
  // Global-centre access = Super Admin / Admin / Accounts / MGT.
  // Everyone else is locked to their assigned centre(s) — matches backend
  // `_has_global_center_access` so the frontend never displays choices the
  // API will reject.
  const hasGlobalCenterAccess = Boolean(
    session && (
      session.is_super_admin || session.is_admin || session.is_accounts ||
      String(session.role_key || "").toLowerCase() === "mgt" ||
      roleMap.mgt === true || roleMap.management === true
    )
  );
  const allowedCenters = useMemo(() => {
    if (hasGlobalCenterAccess) return null; // unrestricted
    const set = new Set();
    for (const k of ["center", "franchise_center", "assigned_center"]) {
      const v = session?.[k];
      if (v) set.add(String(v).toUpperCase());
    }
    for (const listKey of ["centers", "assigned_centers", "allowed_centers"]) {
      for (const v of (session?.[listKey] || [])) {
        if (v) set.add(String(v).toUpperCase());
      }
    }
    return Array.from(set);
  }, [session, hasGlobalCenterAccess]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/config/currency-map`); const d = await r.json();
        let list = Object.keys(d.centers || {});
        // Enforce centre-access restriction on the picker — Center Manager,
        // Accounts (centre-scoped), Ops user, single-centre Franchise owner
        // can only see their own centre. Backend also rejects any bypass.
        if (allowedCenters && allowedCenters.length > 0) {
          list = list.filter(c => allowedCenters.includes(String(c).toUpperCase()));
        }
        setCenters(list);
        if (list.length && !center) setCenter(session?.center || list[0]);
      } catch { /* keep empty */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedCenters]);

  const fmt = (v) => cur.format(v);
  const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

  return (
    <div className="p-4 space-y-4" data-testid="consumption-food-cost-page">
      <div className="flex items-center gap-3">
        <Utensils className="w-6 h-6 text-[#8B0000]" />
        <h1 className="text-2xl font-bold">Consumption &amp; Food Cost</h1>
        <Badge variant="outline" className="ml-2">{cur.symbol} {cur.code}</Badge>
      </div>

      {/* Top filters */}
      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Center</Label>
            {centers.length <= 1 && !hasGlobalCenterAccess ? (
              <div
                className="w-40 h-9 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700"
                data-testid="cfc-center-locked"
                title="Your account is scoped to this centre"
              >
                {center || centers[0] || "—"}
              </div>
            ) : (
              <Select value={center} onValueChange={setCenter}>
                <SelectTrigger className="w-40" data-testid="cfc-center"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div><Label className="text-xs">From</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="cfc-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="cfc-to" /></div>
          <Button size="sm" variant="outline" onClick={() => { setFromDate(today()); setToDate(today()); }}>Today</Button>
          <Button size="sm" variant="outline" onClick={() => { setFromDate(yesterday()); setToDate(yesterday()); }}>Yesterday</Button>
          <Button size="sm" variant="outline" onClick={() => { setFromDate(firstOfMonth()); setToDate(today()); }}>This Month</Button>
        </div>
      </CardContent></Card>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start w-full gap-1 p-1">
          <TabsTrigger value="overview" className="px-4 py-2" data-testid="cfc-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="grid" className="px-4 py-2" data-testid="cfc-tab-grid">Consumption Grid</TabsTrigger>
          <TabsTrigger value="wastage" className="px-4 py-2" data-testid="cfc-tab-wastage">Prepared Food Wastage</TabsTrigger>
          <TabsTrigger value="planner" className="px-4 py-2" data-testid="cfc-tab-planner">Purchase Planner</TabsTrigger>
          <TabsTrigger value="invoices" className="px-4 py-2" data-testid="cfc-tab-invoices">Invoice Upload</TabsTrigger>
          <TabsTrigger value="items" className="px-4 py-2" data-testid="cfc-tab-items">Item &amp; Price Master</TabsTrigger>
          <TabsTrigger value="reports" className="px-4 py-2" data-testid="cfc-tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab session={session} center={center} fromDate={fromDate} toDate={toDate} fmt={fmt} pct={pct} cur={cur} /></TabsContent>
        <TabsContent value="grid"><GridTab session={session} center={center} isEditor={isGridEditor} fmt={fmt} cur={cur} onOpenInvoice={() => setTab("invoices")} /></TabsContent>
        <TabsContent value="wastage"><PreparedFoodWastageTab session={session} center={center} fmt={fmt} /></TabsContent>
        <TabsContent value="planner"><PurchasePlannerTab session={session} center={center} fmt={fmt} /></TabsContent>
        <TabsContent value="invoices"><InvoiceTab session={session} center={center} isEditor={isGridEditor} fmt={fmt} /></TabsContent>
        <TabsContent value="items"><ItemsTab session={session} center={center} isEditor={isEditor} /></TabsContent>
        <TabsContent value="reports"><ReportsTab session={session} center={center} fromDate={fromDate} toDate={toDate} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═════════════════════════ OVERVIEW ═════════════════════════
function OverviewTab({ session, center, fromDate, toDate, fmt, pct, cur }) {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const [kpiOpen, setKpiOpen] = useState(() => localStorage.getItem("cfc_kpi_open") !== "0");
  const load = useCallback(async () => {
    if (!center) return; setLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/overview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, from_date: fromDate, to_date: toDate }),
      });
      if (!r.ok) throw new Error("failed");
      setData(await r.json());
    } catch { toast.error("Failed to load overview"); } finally { setLoading(false); }
  }, [session.token, center, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const t = data.tiles || {};
  const varPositive = (t.variance_amount || 0) > 0;
  const K = [["Items Consumed", `${t.items_consumed || 0}`, `of ${t.days} day(s)`, ShoppingBasket, "tile-items-consumed"],
    ["Total Qty", `${t.total_consumption_qty || 0} units`, `${t.days} day(s)`, Boxes, "tile-total-qty"],
    ["Total Cost", fmt(t.total_consumption_cost), pct(t.total_food_cost_pct) + " of sales", Coins, "tile-total-cost"],
    ["Guest Cost", fmt(t.guest_consumption_cost), pct(t.guest_share_pct) + " of total", Users, "tile-guest-cost"],
    ["Staff Cost", fmt(t.staff_consumption_cost), pct(t.staff_share_pct) + " of total", ChefHat, "tile-staff-cost"],
    ["Guest Food %", pct(t.guest_food_cost_pct), "vs sales", ConciergeBell, "tile-guest-pct"],
    ["Staff Food %", pct(t.staff_food_cost_pct), "vs sales", Utensils, "tile-staff-pct"],
    ["Total Food %", pct(t.total_food_cost_pct), "vs sales", UtensilsCrossed, "tile-total-pct"],
    ["Target %", pct(t.target_food_cost_pct), "from master", Target, "tile-target-pct"],
    ["Variance vs Target", (t.sales_total || 0) === 0 ? "—" : `${varPositive ? "+" : ""}${pct(t.variance_pct)}`,
      (t.sales_total || 0) === 0 ? "No sales entered for this range" : `${fmt(t.variance_amount)} ${varPositive ? "above" : "below"} target`, varPositive ? TrendingUp : TrendingDown, "tile-variance"],
    ["Avg Daily Cost", fmt(t.average_daily_cost), "per day", Coins, "tile-avg-daily"],
    ["Avg / Guest", fmt(t.avg_per_guest), `${t.guest_count} guests`, Users, "tile-avg-guest"],
    ["Avg / Bill", fmt(t.avg_per_bill), `${t.bill_count} bills`, ReceiptText, "tile-avg-bill"]];
  return (
    <div className="space-y-4">
      <button type="button" className="bi-pill" onClick={() => { localStorage.setItem("cfc_kpi_open", kpiOpen ? "0" : "1"); setKpiOpen(!kpiOpen); }} data-testid="cfc-kpi-toggle">
        {kpiOpen ? "Hide KPI blocks" : "Show KPI blocks"}
      </button>
      {kpiOpen && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="cfc-tiles">
          {K.map(([l, v, sub, I, tid], i) => <MetalKpi key={tid} i={i} label={l} value={v} sub={sub} Icon={I} testid={tid} />)}
        </div>)}

      {/* Item-wise tiles — HIDDEN until Recipe Master / Menu Mapping / POS
       Sales Mapping / Standard Recipe Yield / Food Cost Engine are live.
       Without those modules the "High Guest Use / High Staff Use / Variance"
       badges are assumptions on top of manually-entered qty which can mislead
       managers. Code is retained (see git history) for a future re-enable
       once the Consumption Intelligence Dashboard is built on real recipe
       data. Until then the Overview only shows factually-derived totals. */}
      {false && (
      <div>
        <h3 className="font-bold text-slate-800 mb-2">Item-wise Consumption</h3>
        {(data.items || []).length === 0 ? (
          <p className="text-slate-500 text-sm">No consumption entries in this range yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="item-tiles">
            {data.items.slice(0, 30).map(it => (
              <Card key={it.item_code} data-testid={`item-tile-${it.item_code}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-bold text-slate-800">{it.item_name}</p>
                    <Badge className={
                      it.status === "Within Range" ? "bg-emerald-100 text-emerald-700" :
                      it.status === "High Guest Use" ? "bg-amber-100 text-amber-700" :
                      "bg-sky-100 text-sky-700"
                    }>{it.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">Total: <span className="font-semibold">{it.total_qty} {it.unit} · {fmt(it.total_cost)}</span></p>
                  <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                    <div><span className="text-amber-700">Guest:</span> {it.guest_qty} · {fmt(it.guest_cost)} <span className="text-slate-400">({pct(it.guest_share_pct)})</span></div>
                    <div><span className="text-indigo-700">Staff:</span> {it.staff_qty} · {fmt(it.staff_cost)} <span className="text-slate-400">({pct(it.staff_share_pct)})</span></div>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Target Guest: {pct(it.expected_guest_pct)} · Variance: {pct(it.variance_pct)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Daily trend (mini table — no chart lib to keep bundle small this phase) */}
      {(data.trend || []).length > 0 && (
        <Card><CardContent className="p-4">
          <h3 className="mb-3 font-serif text-2xl font-bold text-[#750817]">Daily Trend</h3>
          <div className="overflow-x-auto">
            <Table className="text-base">
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead className="text-right">Total Cost</TableHead>
                <TableHead className="text-right">Guest</TableHead><TableHead className="text-right">Staff</TableHead>
                <TableHead className="text-right">Sales</TableHead><TableHead className="text-right">Food %</TableHead>
              </TableRow></TableHeader>
              <TableBody>{data.trend.map(row => (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell className="text-right">{fmt(row.total_cost)}</TableCell>
                  <TableCell className="text-right">{fmt(row.guest_cost)}</TableCell>
                  <TableCell className="text-right">{fmt(row.staff_cost)}</TableCell>
                  <TableCell className="text-right">{fmt(row.sales)}</TableCell>
                  <TableCell className="text-right">{pct(row.food_cost_pct)}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

// ═════════════════════════ CONSUMPTION GRID ═════════════════════════
function GridTab({ session, center, isEditor, fmt, onOpenInvoice }) {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(false); const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [view, setView] = useState("both");
  const [gridOpen, setGridOpen] = useState(false);
  const [unitFilter, setUnitFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // all | active | wap-pending
  const [sort, setSort] = useState({ key: "item_name", dir: "asc" }); // Excel-style header sort
  // ── Server-authoritative lock & role state (never trust the visible badge) ──
  const [lock, setLock] = useState(null);   // { editable, state, reason, unlock }
  const [userRole, setUserRole] = useState(null); // { is_super_admin, is_admin, ... }
  const [unlockOpen, setUnlockOpen] = useState(false);
  // iter200 — packaging usage warning state
  const [pkgCheck, setPkgCheck] = useState(null);          // /packaging-check response
  const [ackDialog, setAckDialog] = useState(false);
  const [ackReason, setAckReason] = useState('');
  const packagingCodesRef = useRef([]);
  // iter201 — wastage SoT integration
  const [wastageRecon, setWastageRecon] = useState(null);    // /wastage-reconciliation
  const [wastageDetail, setWastageDetail] = useState(null);  // {item_code, ...} → open modal
  const [wastageDetailRows, setWastageDetailRows] = useState(null);
  useEffect(() => {
    if (!wastageDetail || !center || !date) return;
    (async () => {
      setWastageDetailRows(null);
      try {
        const r = await fetch(`${API}/api/consumption/wastage-details`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: session.token, center, date, item_code: wastageDetail.item_code }),
        });
        const d = await r.json();
        if (d.success) setWastageDetailRows(d);
      } catch { /* non-fatal */ }
    })();
  }, [wastageDetail, session.token, center, date]);
  const loadWastageRecon = useCallback(async () => {
    if (!center || !date) return;
    try {
      const r = await fetch(`${API}/api/consumption/wastage-reconciliation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, date }),
      });
      const d = await r.json();
      if (d.success) setWastageRecon(d);
    } catch { /* non-fatal */ }
  }, [session.token, center, date]);
  useEffect(() => { loadWastageRecon(); }, [loadWastageRecon]);

  const loadPkgCheck = useCallback(async () => {
    if (!center || !date) return;
    try {
      const r = await fetch(`${API}/api/consumption/packaging-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, date }),
      });
      const d = await r.json();
      if (d.success) {
        setPkgCheck(d);
        packagingCodesRef.current = d.packaging?.item_codes || [];
      }
    } catch { /* non-fatal */ }
  }, [session.token, center, date]);

  const load = useCallback(async () => {
    if (!center) return; setLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/grid/fetch`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, date }),
      });
      const d = await r.json();
      setRows(d.rows || []);
      setLock(d.lock || null);
      setUserRole(d.user_role || null);
    } catch { toast.error("Failed to load grid"); } finally { setLoading(false); }
  }, [session.token, center, date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPkgCheck(); }, [loadPkgCheck]);

  // Server-derived edit permission is the source of truth.
  const isServerAdmin = Boolean(userRole?.is_super_admin || userRole?.is_admin);
  const dateEditable = lock?.editable !== false;   // null/undefined ⇒ default to true
  const canEdit = Boolean(isEditor && dateEditable);

  const update = (i, f, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  const num = (v) => Number(v || 0);
  // wap_missing = the Item-Master's official WAP is 0 (invoice pending).
  // This flag stays true even after the manager types a temporary rate — that's
  // what keeps the Rate cell editable and drives the "Temporary Rate Used" badge.
  const isWapMissing = (r) => Boolean(r.wap_missing);
  const compute = (r) => {
    const totalQty = num(r.staff_qty) + num(r.guest_qty) + num(r.wastage_qty);
    const avail = num(r.opening_qty) + num(r.qty_received);
    const price = num(r.unit_price);
    return {
      totalQty, avail,
      closing: avail - totalQty,
      cost: totalQty * price,
      pending: price <= 0 && totalQty > 0,
    };
  };

  // Category / unit distinct values for filter dropdowns
  const categories = useMemo(
    () => Array.from(new Set(rows.map(r => r.category).filter(Boolean))).sort(),
    [rows],
  );
  const units = useMemo(
    () => Array.from(new Set(rows.map(r => r.unit).filter(Boolean))).sort(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = rows.map((r, i) => ({ ...r, __i: i })).filter(r => {
      if (q && !((r.item_name || "").toLowerCase().includes(q) ||
                 (r.item_code || "").toLowerCase().includes(q))) return false;
      if (catFilter && String(r.category || "Uncategorized").toLowerCase() !== catFilter.toLowerCase()) return false;
      if (unitFilter && String(r.unit || "").toLowerCase() !== unitFilter.toLowerCase()) return false;
      if (statusFilter === "wap-pending" && !isWapMissing(r)) return false;
      return true;
    });
    // ── Excel-style sort: text A-Z/Z-A, numeric asc/desc, computed columns too ──
    const key = sort?.key || "item_name";
    const dir = sort?.dir === "desc" ? -1 : 1;
    const numericKeys = new Set([
      "opening_qty", "qty_received", "staff_qty", "guest_qty", "wastage_qty",
      "total_qty", "closing_qty", "unit_price", "cost",
    ]);
    const derive = (r) => {
      if (key === "total_qty") return num(r.staff_qty) + num(r.guest_qty) + num(r.wastage_qty);
      if (key === "closing_qty") {
        const c = compute(r); return c.closing;
      }
      if (key === "cost") { const c = compute(r); return c.cost; }
      if (key === "status") {
        const missing = isWapMissing(r);
        if (!missing) return "Priced";
        return num(r.unit_price) > 0 ? "Temporary Rate Used" : "Invoice Required";
      }
      return r[key];
    };
    const sorted = [...base].sort((a, b) => {
      const av = derive(a); const bv = derive(b);
      if (numericKeys.has(key)) return (num(av) - num(bv)) * dir;
      return String(av || "").localeCompare(String(bv || ""), undefined, { sensitivity: "base" }) * dir;
    });
    return sorted;
  }, [rows, search, catFilter, unitFilter, statusFilter, sort]);

  // TOTAL row — recalculated across *filtered* rows only
  const totals = useMemo(() => {
    const acc = { opening: 0, recv: 0, staff: 0, guest: 0, wastage: 0, total: 0, closing: 0, cost: 0 };
    for (const r of filteredRows) {
      const c = compute(r);
      acc.opening += num(r.opening_qty);
      acc.recv += num(r.qty_received);
      acc.staff += num(r.staff_qty);
      acc.guest += num(r.guest_qty);
      acc.wastage += num(r.wastage_qty);
      acc.total += c.totalQty;
      acc.closing += c.closing;
      acc.cost += c.cost;
    }
    return acc;
  }, [filteredRows]);

  const toggleSort = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const save = async (opts = {}) => {
    // iter200 — packaging usage sanity check (non-blocking).
    if (!opts.skipPackagingCheck && pkgCheck?.warning) {
      setAckDialog(true);
      return;
    }
    // ── Client-side pre-check: any consumed row awaiting invoice must have a
    // temporary rate; otherwise block save with a clear message. This matches
    // the server-side rule and gives instant feedback.
    const missingTemp = rows.filter(r =>
      isWapMissing(r) &&
      (num(r.staff_qty) + num(r.guest_qty) + num(r.wastage_qty)) > 0 &&
      num(r.unit_price) <= 0
    );
    if (missingTemp.length > 0) {
      const first = missingTemp[0];
      toast.error(
        `Enter a Temporary Rate for "${first.item_name || first.item_code}" — ` +
        `consumption is recorded but invoice/rate is pending.`
      );
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/consumption/grid/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, date, rows }),
      });
      const d = await r.json();
      if (!r.ok) {
        const detail = d.detail;
        if (detail && typeof detail === "object") {
          if (detail.error === "date_locked") {
            toast.error(detail.message || "Date is locked. Ask an Admin to unlock.");
            return;
          }
          if (detail.error === "consumption_exceeds_available") {
            const msg = detail.rows?.[0]?.message || detail.message;
            toast.error(msg || "Consumption is greater than available stock.");
            return;
          }
          if (detail.error === "temporary_rate_required") {
            const msg = detail.rows?.[0]?.message || detail.message;
            toast.error(msg || "Temporary rate required for consumed items awaiting invoice.");
            return;
          }
        }
        throw new Error(typeof detail === "string" ? detail : (detail?.message || "Save failed"));
      }
      const c = d.cascade || {};
      toast.success(
        `Saved ${d.saved} row${d.saved === 1 ? "" : "s"}` +
        (c.dates_recomputed ? ` · recomputed ${c.dates_recomputed} future entr${c.dates_recomputed === 1 ? "y" : "ies"}` : "")
      );
      await load();
      await loadPkgCheck();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  // iter200 — SA-confirmed "no packaging" ack (audited).
  const submitPackagingAck = async () => {
    if (!ackReason.trim()) { toast.error('A short reason is required'); return; }
    try {
      const r = await fetch(`${API}/api/consumption/packaging-ack`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, date, reason: ackReason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Ack failed');
      toast.success('Confirmation recorded');
      setAckDialog(false); setAckReason('');
      await loadPkgCheck();
      // proceed with save now that ack is on file
      await save({ skipPackagingCheck: true });
    } catch (e) { toast.error(e.message); }
  };

  const showPackagingOnly = () => {
    // Filter grid to packaging items using the same category filter dropdown.
    // If category not consistent across items we fall back to search.
    const cats = Array.from(new Set((pkgCheck?.packaging?.items || []).map(i => i.category).filter(Boolean)));
    if (cats.length === 1) {
      setCatFilter(cats[0]);
      setSearch('');
    } else {
      setSearch('container');
      setCatFilter('');
    }
  };
  const clearPackagingFilter = () => { setCatFilter(''); setSearch(''); };

  const pendingCount = useMemo(() => rows.filter(isWapMissing).length, [rows]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="grid-date" /></div>
        <Button size="sm" variant="outline" onClick={() => setDate(today())}>Today</Button>
        <Button size="sm" variant="outline" onClick={() => setDate(yesterday())}>Yesterday</Button>
        <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
        {isEditor && (
          <Button size="sm" onClick={save} disabled={saving || rows.length === 0 || !canEdit}
                  data-testid="grid-save-all"
                  title={!canEdit ? (lock?.reason || "Date is locked") : ""}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save All
          </Button>
        )}
        {isServerAdmin && lock?.manager_locked && lock?.state !== "unlocked_by_admin" && (
          <Button size="sm" variant="default" className="bg-emerald-700 hover:bg-emerald-800"
                  onClick={() => setUnlockOpen(true)}
                  data-testid="grid-unlock-btn"
                  title="Grant Center Manager edit access for this date">
            🔓 Unlock Period
          </Button>
        )}
        {isServerAdmin && lock?.state === "unlocked_by_admin" && lock?.unlock && (
          <RelockButton session={session} unlockId={lock.unlock._id} onDone={load} />
        )}
      </div>

      {/* Lock / Unlock banner — server-authoritative */}
      {lock && (
        lock.state === "locked" ? (
          <div className="bg-rose-50 border border-rose-300 text-rose-900 rounded p-2 text-xs flex items-center gap-2"
               data-testid="grid-lock-banner">
            <span>🔒 <strong>Locked</strong> — {lock.reason}. {isServerAdmin ? "Click Unlock Period to open editing." : "Ask an Admin to unlock this date to make changes."}</span>
          </div>
        ) : lock.state === "unlocked_by_admin" ? (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 rounded p-2 text-xs"
               data-testid="grid-unlock-banner">
            🔓 <strong>Unlocked for Editing</strong> —{" "}
            By: {lock.unlock?.unlocked_by_name || "Admin"} · Range: {lock.unlock?.from_date}
            {lock.unlock?.to_date !== lock.unlock?.from_date ? ` → ${lock.unlock?.to_date}` : ""}
            {lock.unlock?.expires_at ? ` · Expires: ${lock.unlock.expires_at.slice(0, 16).replace("T", " ")}` : " · No expiry"}
            {lock.unlock?.reason ? ` · Reason: ${lock.unlock.reason}` : ""}
          </div>
        ) : isServerAdmin && lock.manager_locked ? (
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded p-2 text-xs"
               data-testid="grid-admin-locked-hint">
            ⚠️ This date is <strong>locked for Center Managers</strong> (only today &amp; yesterday are editable for them). You can edit as Admin. Click <strong>🔓 Unlock Period</strong> to grant a manager temporary edit access.
          </div>
        ) : null
      )}

      {/* iter200 — Packaging usage warning banner */}
      {pkgCheck?.warning && (
        <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded p-3 text-xs space-y-2"
             data-testid="pkg-warning-banner">
          <div className="flex items-start gap-2">
            <span className="text-lg leading-none">⚠️</span>
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                Parcel / Delivery sales were recorded for this date, but no parcel-container consumption has been entered.
              </div>
              <div className="mt-1 text-[11px] text-amber-800 flex flex-wrap gap-x-3 gap-y-0.5">
                {pkgCheck.sales.swiggy > 0 && <span>Swiggy <strong>₹{pkgCheck.sales.swiggy.toLocaleString('en-IN')}</strong></span>}
                {pkgCheck.sales.zomato > 0 && <span>Zomato <strong>₹{pkgCheck.sales.zomato.toLocaleString('en-IN')}</strong></span>}
                {pkgCheck.sales.total_online_sale > 0 && <span>Online <strong>₹{pkgCheck.sales.total_online_sale.toLocaleString('en-IN')}</strong></span>}
                {pkgCheck.sales.sale_pbm > 0 && <span>PBM/Pickup <strong>₹{pkgCheck.sales.sale_pbm.toLocaleString('en-IN')}</strong></span>}
                {pkgCheck.sales.bills > 0 && <span><strong>{pkgCheck.sales.bills}</strong> bills</span>}
                <span className="text-red-600">Packaging entered: <strong>0</strong> ({pkgCheck.packaging.total_count} configured)</span>
              </div>
              <div className="mt-1 text-[10px] text-amber-700">Please confirm packaging usage before saving consumption.</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7 text-[10px]"
                onClick={showPackagingOnly} data-testid="pkg-show-btn">Show Packaging Items</Button>
              <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                onClick={clearPackagingFilter} data-testid="pkg-clear-btn">Go Back to All Items</Button>
            </div>
          </div>
        </div>
      )}
      {pkgCheck?.confirmation?.confirmed_no_packaging && (
        <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-600" data-testid="pkg-ack-badge">
          ✓ Packaging waived for this date — Reason: <strong>{pkgCheck.confirmation.reason}</strong> · By {pkgCheck.confirmation.by}
        </div>
      )}

      {/* iter201 — wastage reconciliation banner */}
      {wastageRecon?.mismatches?.length > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded p-2 text-xs" data-testid="wastage-recon-banner">
          <div className="flex items-start gap-2">
            <span className="text-lg leading-none">⚠</span>
            <div className="flex-1">
              <div className="font-semibold text-orange-900">WASTAGE RECONCILIATION ERROR — {wastageRecon.mismatches.length} item(s)</div>
              <div className="text-[11px] text-orange-800 mt-0.5">
                The Consumption Grid still holds legacy wastage quantities that no longer match Wastage Management.
                Fix each entry via Wastage Management → Daily Register.
              </div>
              <div className="mt-1 text-[10px] text-orange-800 space-y-0.5 max-h-24 overflow-y-auto">
                {wastageRecon.mismatches.slice(0, 10).map((m, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="font-mono">{m.item_name}</span>
                    <span>WM: <strong>{m.wastage_management_qty}</strong> {m.unit}</span>
                    <span>Grid: <strong>{m.consumption_grid_qty}</strong> {m.unit}</span>
                    <span className="text-red-600">Δ {m.delta}</span>
                  </div>
                ))}
                {wastageRecon.mismatches.length > 10 && (
                  <div className="text-slate-500">…and {wastageRecon.mismatches.length - 10} more</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}



      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <ViewSwitch view={view} setView={(v) => { setView(v); if (v === "grid") setGridOpen(true); }} testid="grid-view" />
        {catFilter && <button type="button" className="bi-pill" data-active="true" onClick={() => setCatFilter("")} data-testid="grid-cat-clear">Category: {catFilter} ✕</button>}
        {view === "both" && <button type="button" className="bi-pill" onClick={() => setGridOpen(!gridOpen)} data-testid="grid-full-toggle">{gridOpen ? "Hide full grid" : "Show full grid"}</button>}
      </div>
      {view !== "grid" && <CategoryBlocks rows={rows} value={catFilter} onChange={setCatFilter} testid="grid-cat" />}

      {/* Search + filters */}
      <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-slate-100">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search item name or code…"
               className="h-8 max-w-xs" data-testid="grid-search" />
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
                className="h-8 text-xs border rounded px-2" data-testid="grid-cat-filter">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
                className="h-8 text-xs border rounded px-2" data-testid="grid-unit-filter">
          <option value="">All units</option>
          {units.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 text-xs border rounded px-2" data-testid="grid-status-filter">
          <option value="all">All items</option>
          <option value="wap-pending">Invoice required (WAP = 0)</option>
        </select>
        {pendingCount > 0 && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">
            {pendingCount} item{pendingCount > 1 ? "s" : ""} awaiting invoice
          </Badge>
        )}
        <div className="ml-auto text-xs text-slate-500" data-testid="grid-count">
          Showing {filteredRows.length} of {rows.length} items
        </div>
      </div>

      {!loading && view !== "grid" && (
        <ConsumptionItemCards rows={filteredRows} update={update} compute={compute} canEdit={canEdit} fmt={fmt}
          total={<p className="text-base text-[#5a3a22]">Edits here and in the grid are the same entries — press <b>Save All</b> once to save.</p>} />)}
      {loading ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
        <Card className={view === "cards" || (view === "both" && !gridOpen) ? "hidden" : ""} data-testid="grid-full-card"><CardContent className="p-0 overflow-x-auto">
          <Table className="text-xs" style={{ tableLayout: "fixed", minWidth: "1500px" }}>
            {/* Fixed column widths — header + TOTAL + data rows all share the same layout */}
            <colgroup><col style={{ width: "160px" }} /><col style={{ width: "100px" }} /><col style={{ width: "130px" }} /><col style={{ width: "70px" }} /><col style={{ width: "90px" }} /><col style={{ width: "90px" }} /><col style={{ width: "95px" }} /><col style={{ width: "95px" }} /><col style={{ width: "95px" }} /><col style={{ width: "85px" }} /><col style={{ width: "85px" }} /><col style={{ width: "80px" }} /><col style={{ width: "90px" }} /><col style={{ width: "100px" }} /><col style={{ minWidth: "160px" }} /></colgroup>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("item_name")} data-testid="grid-sort-item">Item{sortArrow("item_name")}</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("item_code")} data-testid="grid-sort-code">Code{sortArrow("item_code")}</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("category")} data-testid="grid-sort-category">Category{sortArrow("category")}</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("unit")} data-testid="grid-sort-unit">Unit{sortArrow("unit")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("opening_qty")}>Opening{sortArrow("opening_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("qty_received")}>Recv{sortArrow("qty_received")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("staff_qty")}>Staff Qty{sortArrow("staff_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("guest_qty")}>Guest Qty{sortArrow("guest_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("wastage_qty")}>Wastage{sortArrow("wastage_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("total_qty")}>Total{sortArrow("total_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("closing_qty")}>Closing{sortArrow("closing_qty")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("unit_price")}>Rate{sortArrow("unit_price")}</TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("cost")}>Cost{sortArrow("cost")}</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("status")}>Status{sortArrow("status")}</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
              {/* TOTAL row — sticky just below headers; recalculates on filter change */}
              <TableRow className="bg-slate-100 font-semibold sticky top-0 z-10" data-testid="grid-total-row">
                <TableCell className="font-bold text-slate-700">TOTAL</TableCell>
                <TableCell className="text-slate-400 text-center">—</TableCell>
                <TableCell className="text-slate-400 text-center">—</TableCell>
                <TableCell className="text-slate-500 italic text-[10px]">
                  {(() => {
                    const units = Array.from(new Set(filteredRows.map(r => r.unit).filter(Boolean)));
                    return units.length > 1 ? "Multiple Units" : (units[0] || "—");
                  })()}
                </TableCell>
                <TableCell className="text-right">{totals.opening.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.recv.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.staff.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.guest.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.wastage.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.total.toFixed(2)}</TableCell>
                <TableCell className="text-right">{totals.closing.toFixed(2)}</TableCell>
                <TableCell className="text-right text-slate-400">—</TableCell>
                <TableCell className="text-right font-mono" data-testid="grid-total-cost">{fmt(totals.cost)}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>{filteredRows.map((r) => {
              const c = compute(r);
              const wapMissing = isWapMissing(r);
              const rateEntered = num(r.unit_price) > 0;
              // Rate cell rules:
              //   • WAP > 0 (valid) → read-only display of WAP
              //   • WAP == 0 (Invoice Required) → editable temporary rate input
              const rateEditable = canEdit && wapMissing;
              const inputCls = "w-full text-right h-7 px-1";
              const roCls = "w-full text-right h-7 px-1 bg-slate-100 text-slate-500 cursor-not-allowed";
              return (<TableRow key={r.item_code} className={wapMissing ? "bg-amber-50/60" : ""} data-testid={`grid-row-${r.item_code}`}>
                <TableCell className="font-semibold truncate">{r.item_name}</TableCell>
                <TableCell className="font-mono text-[10px] truncate">{r.item_code}</TableCell>
                <TableCell className="truncate">{r.category}</TableCell>
                <TableCell className="truncate">{r.unit}</TableCell>
                <TableCell className="text-right">{r.opening_qty}</TableCell>
                <TableCell><Input type="number" step="0.01" value={r.qty_received} onChange={(e) => update(r.__i, "qty_received", parseFloat(e.target.value) || 0)} disabled={!canEdit} className={canEdit ? inputCls : roCls} data-testid={`grid-recv-${r.item_code}`} /></TableCell>
                <TableCell><Input type="number" step="0.01" value={r.staff_qty} onChange={(e) => update(r.__i, "staff_qty", parseFloat(e.target.value) || 0)} disabled={!canEdit} className={canEdit ? inputCls : roCls} data-testid={`grid-staff-${r.item_code}`} /></TableCell>
                <TableCell><Input type="number" step="0.01" value={r.guest_qty} onChange={(e) => update(r.__i, "guest_qty", parseFloat(e.target.value) || 0)} disabled={!canEdit} className={canEdit ? inputCls : roCls} data-testid={`grid-guest-${r.item_code}`} /></TableCell>
                <TableCell>
                  {/* iter201 — read-only wastage mirror from Wastage Management.  Click to see details. */}
                  <button type="button"
                    onClick={() => setWastageDetail({ item_code: r.item_code, item_name: r.item_name, unit: r.unit })}
                    className={`text-right w-full ${r.wastage_source === 'management' ? 'text-slate-700 font-semibold' : 'text-amber-600'} underline decoration-dotted`}
                    title="Managed in Wastage Management — click to see entries"
                    data-testid={`grid-wastage-${r.item_code}`}>
                    {num(r.wastage_qty).toFixed(2)}
                    {r.wastage_entries > 0 && (
                      <span className="text-[9px] text-slate-400 ml-1">({r.wastage_entries})</span>
                    )}
                  </button>
                </TableCell>
                <TableCell className="text-right font-semibold">{c.totalQty.toFixed(2)}</TableCell>
                <TableCell className="text-right">{c.closing.toFixed(2)}</TableCell>
                <TableCell className="text-right">
                  {rateEditable ? (
                    <Input
                      type="number" step="0.01" min="0"
                      value={r.unit_price || ""}
                      placeholder="Temp"
                      onChange={(e) => update(r.__i, "unit_price", parseFloat(e.target.value) || 0)}
                      className={inputCls + " bg-white"}
                      data-testid={`grid-rate-${r.item_code}`}
                      title="Temporary rate — used until invoice arrives"
                    />
                  ) : (
                    <span data-testid={`grid-rate-ro-${r.item_code}`}>{fmt(r.unit_price)}</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold" data-testid={`grid-cost-${r.item_code}`}>
                  {wapMissing && !rateEntered && c.totalQty > 0
                    ? <span className="text-amber-700 italic text-[11px]">Pending</span>
                    : fmt(c.cost)}
                </TableCell>
                <TableCell>
                  {(() => {
                    if (!wapMissing) {
                      return <Badge variant="outline" className="text-[10px]">Priced</Badge>;
                    }
                    if (rateEntered) {
                      return (
                        <div className="flex flex-col gap-1" data-testid={`grid-status-temp-${r.item_code}`}>
                          <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-400 text-blue-800 whitespace-nowrap">
                            Temporary Rate Used
                          </Badge>
                          <span className="text-[9px] text-slate-500 italic">
                            Invoice still required for final price verification.
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex flex-col gap-1" data-testid={`grid-status-invreq-${r.item_code}`}>
                        <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-400 text-amber-800 whitespace-nowrap">
                          Invoice Required
                        </Badge>
                        {canEdit && (
                          <Button size="sm" variant="link" className="h-5 p-0 text-[10px] text-[#8B0000]"
                                  onClick={() => onOpenInvoice?.({ item_code: r.item_code, item_name: r.item_name })}
                                  data-testid={`grid-attach-invoice-${r.item_code}`}>
                            Attach Invoice
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell><Input value={r.remarks} onChange={(e) => update(r.__i, "remarks", e.target.value)} disabled={!canEdit} className={canEdit ? "w-full h-7 text-xs" : "w-full h-7 text-xs bg-slate-100 text-slate-500 cursor-not-allowed"} /></TableCell>
              </TableRow>);
            })}
            {filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={15} className="text-center text-slate-500 py-6">
                {rows.length === 0 ? "No items configured yet. Add via Item & Price Master." : "No rows match the current search / filters."}
              </TableCell></TableRow>
            )}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
      {/* Admin: Unlock Consumption Period modal */}
      {unlockOpen && (
        <UnlockDialog
          open={unlockOpen}
          onClose={() => setUnlockOpen(false)}
          session={session}
          center={center}
          defaultDate={date}
          onCreated={load}
        />
      )}

      {/* iter200 — packaging ack dialog */}
      <Dialog open={ackDialog} onOpenChange={setAckDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delivery/parcel sales exist but no packaging consumption</DialogTitle>
            <DialogDescription className="text-xs">
              Confirm no packaging was used, or go back and enter quantities.  Every confirmation is audited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="text-slate-600">
              Legitimate cases: customer supplied container · order cancelled/refunded · packaging under another item · no packaging required.
            </div>
            <Label className="text-[10px]">Reason (required)</Label>
            <Input value={ackReason} onChange={(e) => setAckReason(e.target.value)}
              placeholder="e.g., dine-in only day, customer supplied container..."
              className="h-8 text-xs" data-testid="pkg-ack-reason" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setAckDialog(false); showPackagingOnly(); }}
              data-testid="pkg-ack-review">Review Packaging</Button>
            <Button size="sm" onClick={submitPackagingAck} data-testid="pkg-ack-confirm">Confirm No Packaging Used</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* iter201 — wastage details modal (click-through from Grid) */}
      <Dialog open={!!wastageDetail} onOpenChange={(o) => !o && setWastageDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Wastage Details — {wastageDetail?.item_name}</DialogTitle>
            <DialogDescription className="text-xs">
              {date} · Managed exclusively in Wastage Management.  Edit / correct via <em>Prepared Food Wastage → Daily Register</em>.
            </DialogDescription>
          </DialogHeader>
          {wastageDetailRows ? (
            wastageDetailRows.entries.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">No wastage entries recorded for this item on this date.</div>
            ) : (
              <div className="space-y-2">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100"><tr>
                    <th className="text-right p-1">Qty</th><th className="text-left p-1">Reason</th>
                    <th className="text-left p-1">Remark</th><th className="text-right p-1">Cost ₹</th>
                    <th className="text-left p-1">By</th>
                  </tr></thead>
                  <tbody>
                    {wastageDetailRows.entries.map((e, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="text-right p-1"><strong>{e.quantity}</strong> {e.unit || ''}</td>
                        <td className="p-1">{e.reason}</td>
                        <td className="p-1 text-slate-500 truncate max-w-[120px]">{e.remark || ''}</td>
                        <td className="text-right p-1">{fmt(e.food_cost_wastage)}</td>
                        <td className="p-1 text-slate-500 text-[10px]">{e.created_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-xs border-t pt-1 flex justify-between">
                  <span>Total wastage: <strong>{wastageDetailRows.total_qty}</strong> {wastageDetail?.unit}</span>
                  <span>Cost: <strong>₹{wastageDetailRows.total_cost}</strong></span>
                </div>
              </div>
            )
          ) : (
            <div className="text-xs text-slate-400 py-4 text-center">Loading…</div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setWastageDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ═════════════════════════ UNLOCK / RELOCK ═════════════════════════
function UnlockDialog({ open, onClose, session, center, defaultDate, onCreated }) {
  const [type, setType] = useState("single");   // single | date_range | month | month_range
  const [fromDate, setFromDate] = useState(defaultDate);
  const [toDate, setToDate] = useState(defaultDate);
  const [fromMonth, setFromMonth] = useState(defaultDate.slice(0, 7));
  const [toMonth, setToMonth] = useState(defaultDate.slice(0, 7));
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    const body = {
      token: session.token, center,
      unlock_type: type,
      reason: reason.trim(),
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    };
    if (type === "single") { body.from_date = fromDate; }
    else if (type === "date_range") { body.from_date = fromDate; body.to_date = toDate; }
    else if (type === "month") { body.from_month = fromMonth; }
    else { body.from_month = fromMonth; body.to_month = toMonth; }

    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/unlocks/create`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Unlock failed");
      toast.success("Consumption period unlocked");
      onCreated?.(); onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="unlock-dialog">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800">🔓 Unlock Consumption Period</h3>
            <button className="text-slate-500 hover:text-slate-800" onClick={onClose}>✕</button>
          </div>
          <p className="text-xs text-slate-500">
            Center: <strong>{center}</strong>. The selected Center Manager will regain edit access for the chosen window.
          </p>
          <div>
            <Label className="text-xs">Unlock Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    className="w-full h-8 border rounded px-2 text-xs mt-1"
                    data-testid="unlock-type">
              <option value="single">Single Date</option>
              <option value="date_range">Date Range</option>
              <option value="month">Single Month</option>
              <option value="month_range">Month Range</option>
            </select>
          </div>
          {type === "single" && (
            <div><Label className="text-xs">Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="unlock-from-date" />
            </div>
          )}
          {type === "date_range" && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="unlock-from-date" />
              </div>
              <div><Label className="text-xs">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="unlock-to-date" />
              </div>
            </div>
          )}
          {type === "month" && (
            <div><Label className="text-xs">Month</Label>
              <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} data-testid="unlock-from-month" />
            </div>
          )}
          {type === "month_range" && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">From Month</Label>
                <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} data-testid="unlock-from-month" />
              </div>
              <div><Label className="text-xs">To Month</Label>
                <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} data-testid="unlock-to-month" />
              </div>
            </div>
          )}
          <div><Label className="text-xs">Reason<span className="text-rose-600">*</span></Label>
            <select
              value={reason.startsWith("__custom__") ? "__custom__" : reason}
              onChange={(e) => setReason(e.target.value === "__custom__" ? "" : e.target.value)}
              className="w-full h-8 border rounded px-2 text-xs mt-1"
              data-testid="unlock-reason-preset"
            >
              <option value="">Select a reason…</option>
              <option>Incorrect quantity entered</option>
              <option>Missing receipt</option>
              <option>Wrong guest/staff allocation</option>
              <option>Opening balance correction</option>
              <option>Audit adjustment</option>
              <option value="__custom__">Other (specify below)</option>
            </select>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Or type a reason…"
              className="mt-2"
              data-testid="unlock-reason"
            />
          </div>
          <div><Label className="text-xs">Unlock Until (optional)</Label>
            <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="unlock-expires-at" />
            <p className="text-[10px] text-slate-400 mt-1">Leave blank to unlock indefinitely (until manually relocked).</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy || !reason.trim()} data-testid="unlock-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : "🔓 Confirm Unlock"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RelockButton({ session, unlockId, onDone }) {
  const [busy, setBusy] = useState(false);
  const relock = async () => {
    if (!window.confirm("Relock this consumption period? The Center Manager will lose edit access immediately.")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/unlocks/relock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, unlock_id: unlockId, reason: "Manual relock from grid" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Relock failed");
      toast.success("Period relocked");
      onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-50"
            onClick={relock} disabled={busy} data-testid="grid-relock-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : "🔒 Relock"}
    </Button>
  );
}

// ═════════════════════════ INVOICES ═════════════════════════
function InvoiceTab({ session, center, isEditor, fmt }) {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [invoices, setInvoices] = useState([]); const [tot, setTot] = useState({});
  const [form, setForm] = useState({ invoice_date: today(), invoice_number: "", vendor: "" });
  const [file, setFile] = useState(null); const [uploading, setUploading] = useState(false);
  // OCR (Phase 2) — PDF / image preview flow
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null);   // { invoice_date, invoice_number, vendor, items[] }
  const [ocrSaving, setOcrSaving] = useState(false);
  const [itemCodes, setItemCodes] = useState([]);       // for mapping OCR names → master codes

  const load = useCallback(async () => {
    if (!center) return;
    const r = await fetch(`${API}/api/consumption/invoices/list`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center, month }),
    });
    const d = await r.json();
    setInvoices(d.invoices || []); setTot({ total_invoices: d.total_invoices, total_value: d.total_value, total_qty: d.total_qty });
  }, [session.token, center, month]);
  useEffect(() => { load(); }, [load]);

  // Load item master (for OCR item_code mapping datalist)
  useEffect(() => {
    if (!center) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/consumption/items/list`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: session.token, center }),
        });
        const d = await r.json();
        setItemCodes((d.items || []).map(i => ({ code: i.item_code, name: i.item_name })));
      } catch { /* ignore */ }
    })();
  }, [session.token, center]);

  const upload = async () => {
    if (!file || !form.invoice_number || !form.vendor) { toast.error("Fill invoice # + vendor + file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("token", session.token); fd.append("center", center);
      fd.append("invoice_date", form.invoice_date); fd.append("invoice_number", form.invoice_number);
      fd.append("vendor", form.vendor); fd.append("file", file);
      const r = await fetch(`${API}/api/consumption/invoices/upload`, { method: "POST", body: fd });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Upload failed");
      toast.success(`Uploaded — ${d.lines} lines, ${fmt(d.total_value)}. Prices updated.`);
      setFile(null); setForm({ ...form, invoice_number: "" }); await load();
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  };

  // ─── OCR flow ───
  const runOcr = async () => {
    if (!ocrFile) { toast.error("Choose a PDF or image first"); return; }
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("token", session.token); fd.append("center", center); fd.append("file", ocrFile);
      const r = await fetch(`${API}/api/consumption/invoices/parse-vision`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "OCR failed");
      setOcrPreview({
        invoice_date: d.invoice_date || today(),
        invoice_number: d.invoice_number || "",
        vendor: d.vendor || "",
        items: d.items || [],
      });
      toast.success(`Parsed ${d.items?.length || 0} lines — review & save`);
    } catch (e) { toast.error(e.message); } finally { setOcrLoading(false); }
  };

  const updateOcrItem = (i, f, v) => setOcrPreview(p => ({
    ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [f]: v } : it),
  }));
  const removeOcrItem = (i) => setOcrPreview(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const addOcrItem = () => setOcrPreview(p => ({
    ...p, items: [...p.items, { item_code: "", item_name: "", qty: 0, unit: "kg", rate: 0, tax: 0, discount: 0, freight: 0, net_cost: 0 }],
  }));

  const saveOcr = async () => {
    if (!ocrPreview) return;
    if (!ocrPreview.vendor || !ocrPreview.invoice_number) { toast.error("Vendor + invoice # required"); return; }
    if (!ocrPreview.items.length) { toast.error("No lines to save"); return; }
    setOcrSaving(true);
    try {
      // Recompute net_cost so user edits reflect on server
      const items = ocrPreview.items.map(it => {
        const qty = Number(it.qty || 0); const rate = Number(it.rate || 0);
        const tax = Number(it.tax || 0); const disc = Number(it.discount || 0);
        const freight = Number(it.freight || 0);
        const net = qty * rate + tax + freight - disc;
        return { ...it, qty, rate, tax, discount: disc, freight, net_cost: Number(net.toFixed(2)) };
      });
      const r = await fetch(`${API}/api/consumption/invoices/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center,
          invoice_date: ocrPreview.invoice_date, invoice_number: ocrPreview.invoice_number,
          vendor: ocrPreview.vendor, items,
        }),
      });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Save failed");
      toast.success(`Saved — ${d.lines} lines, ${fmt(d.total_value)}. Prices updated.`);
      setOcrPreview(null); setOcrFile(null); await load();
    } catch (e) { toast.error(e.message); } finally { setOcrSaving(false); }
  };

  return (<div className="space-y-3">
    <div className="flex flex-wrap items-end gap-2">
      <div><Label className="text-xs">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="inv-month" /></div>
      <div className="ml-auto flex gap-4 text-xs">
        <div>Invoices: <span className="font-bold">{tot.total_invoices || 0}</span></div>
        <div>Total value: <span className="font-bold">{fmt(tot.total_value || 0)}</span></div>
        <div>Total qty: <span className="font-bold">{tot.total_qty || 0}</span></div>
      </div>
    </div>
    {isEditor && (
      <Card><CardContent className="p-3 space-y-2">
        <p className="font-semibold text-sm">Upload New Invoice (Excel / CSV)</p>
        <div className="grid grid-cols-4 gap-2">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-date" /></div>
          <div><Label className="text-xs">Invoice #</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="inv-number" /></div>
          <div><Label className="text-xs">Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="inv-vendor" /></div>
          <div><Label className="text-xs">File</Label><Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="inv-file" /></div>
        </div>
        <Button size="sm" onClick={upload} disabled={uploading} data-testid="inv-upload">
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload &amp; Update Prices
        </Button>
        <p className="text-[10px] text-slate-500">Columns expected: item_code, item_name, qty, unit, rate, tax, discount, freight.</p>
      </CardContent></Card>
    )}

    {/* ─── OCR (PDF / Image) ─── */}
    {isEditor && (
      <Card className="border-amber-300 bg-amber-50/40"><CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <p className="font-semibold text-sm">AI Invoice OCR — PDF or Image (Gemini vision)</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">File (PDF / JPG / PNG)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e) => setOcrFile(e.target.files?.[0])} data-testid="ocr-file" className="w-72" />
          </div>
          <Button size="sm" onClick={runOcr} disabled={ocrLoading || !ocrFile} data-testid="ocr-run">
            {ocrLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {ocrLoading ? "Extracting…" : "Extract Lines"}
          </Button>
          {ocrPreview && (<Button size="sm" variant="ghost" onClick={() => setOcrPreview(null)}>Discard preview</Button>)}
        </div>
        <p className="text-[10px] text-slate-500">Upload a scanned or PDF invoice. Gemini extracts vendor + line items. Review, map item codes, then save — prices update automatically.</p>

        {ocrPreview && (
          <div className="space-y-2 pt-2 border-t border-amber-200">
            <div className="grid grid-cols-4 gap-2">
              <div><Label className="text-xs">Vendor *</Label><Input value={ocrPreview.vendor} onChange={(e) => setOcrPreview({ ...ocrPreview, vendor: e.target.value })} data-testid="ocr-vendor" /></div>
              <div><Label className="text-xs">Invoice # *</Label><Input value={ocrPreview.invoice_number} onChange={(e) => setOcrPreview({ ...ocrPreview, invoice_number: e.target.value })} data-testid="ocr-invoice-number" /></div>
              <div><Label className="text-xs">Invoice Date</Label><Input type="date" value={ocrPreview.invoice_date} onChange={(e) => setOcrPreview({ ...ocrPreview, invoice_date: e.target.value })} data-testid="ocr-invoice-date" /></div>
              <div className="flex items-end">
                <Button size="sm" onClick={saveOcr} disabled={ocrSaving} data-testid="ocr-save">
                  {ocrSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Invoice
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader><TableRow>
                  <TableHead>Item Code</TableHead><TableHead>Name</TableHead>
                  <TableHead className="text-right">Qty</TableHead><TableHead>Unit</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Tax</TableHead><TableHead className="text-right">Disc</TableHead>
                  <TableHead className="text-right">Freight</TableHead><TableHead className="text-right">Net</TableHead>
                  <TableHead />
                </TableRow></TableHeader>
                <TableBody>{ocrPreview.items.map((it, i) => {
                  const net = Number(it.qty || 0) * Number(it.rate || 0) + Number(it.tax || 0) + Number(it.freight || 0) - Number(it.discount || 0);
                  return (<TableRow key={i} data-testid={`ocr-row-${i}`}>
                    <TableCell><Input list="ocr-codes" value={it.item_code || ""} onChange={(e) => updateOcrItem(i, "item_code", e.target.value)} className="w-24 h-7" data-testid={`ocr-code-${i}`} /></TableCell>
                    <TableCell><Input value={it.item_name} onChange={(e) => updateOcrItem(i, "item_name", e.target.value)} className="w-40 h-7" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={it.qty} onChange={(e) => updateOcrItem(i, "qty", parseFloat(e.target.value) || 0)} className="w-20 h-7 text-right" /></TableCell>
                    <TableCell><Input value={it.unit} onChange={(e) => updateOcrItem(i, "unit", e.target.value)} className="w-16 h-7" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={it.rate} onChange={(e) => updateOcrItem(i, "rate", parseFloat(e.target.value) || 0)} className="w-20 h-7 text-right" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={it.tax} onChange={(e) => updateOcrItem(i, "tax", parseFloat(e.target.value) || 0)} className="w-16 h-7 text-right" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={it.discount} onChange={(e) => updateOcrItem(i, "discount", parseFloat(e.target.value) || 0)} className="w-16 h-7 text-right" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={it.freight} onChange={(e) => updateOcrItem(i, "freight", parseFloat(e.target.value) || 0)} className="w-16 h-7 text-right" /></TableCell>
                    <TableCell className="text-right font-mono">{fmt(net)}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => removeOcrItem(i)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button></TableCell>
                  </TableRow>);
                })}</TableBody>
              </Table>
              <datalist id="ocr-codes">
                {itemCodes.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </datalist>
            </div>
            <Button size="sm" variant="outline" onClick={addOcrItem} data-testid="ocr-add-line"><Plus className="w-3.5 h-3.5 mr-1" /> Add Line</Button>
          </div>
        )}
      </CardContent></Card>
    )}

    <Card><CardContent className="p-0 overflow-x-auto">
      <Table className="text-xs">
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Invoice #</TableHead><TableHead>Vendor</TableHead>
          <TableHead className="text-right">Lines</TableHead><TableHead className="text-right">Qty</TableHead>
          <TableHead className="text-right">Value</TableHead><TableHead>File</TableHead>
        </TableRow></TableHeader>
        <TableBody>{invoices.map((inv) => (
          <TableRow key={inv.invoice_id}>
            <TableCell>{inv.invoice_date}</TableCell><TableCell>{inv.invoice_number}</TableCell>
            <TableCell>{inv.vendor}</TableCell>
            <TableCell className="text-right">{inv.items?.length || 0}</TableCell>
            <TableCell className="text-right">{inv.total_qty}</TableCell>
            <TableCell className="text-right font-mono">{fmt(inv.total_value)}</TableCell>
            <TableCell className="text-slate-500">{inv.file_name}</TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </CardContent></Card>
  </div>);
}

// ═════════════════════════ ITEM MASTER ═════════════════════════
function ItemsTab({ session, center, isEditor }) {
  const [items, setItems] = useState([]); const [dlg, setDlg] = useState(null); const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteDlg, setDeleteDlg] = useState(null); // { item, usage: {consumption_entries, invoice_lines, can_hard_delete} }
  const [deleting, setDeleting] = useState(false);
  // ── Bulk selection + bulk-action state ──
  const [selected, setSelected] = useState({}); // { item_code: bool }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [catDlg, setCatDlg] = useState(null);   // { category, summary }
  const [bulkAllDlg, setBulkAllDlg] = useState(null); // { count, reason, confirmText }
  const [actionSummary, setActionSummary] = useState(null); // last bulk result
  const isSuperAdmin = Boolean(session?.is_super_admin);
  // ── Manager price-update state ─────────────────────────────
  const [priceAccessList, setPriceAccessList] = useState([]);   // active windows for this centre
  const [priceEditDlg, setPriceEditDlg] = useState(null);       // { item, newPrice, vendor, remarks, proof, confirm }
  const [priceSubmitting, setPriceSubmitting] = useState(false);
  const isCenterManagerReadOnly = !isEditor;                     // Managers see the tab but can't edit

  const load = useCallback(async () => {
    if (!center) return;
    const r = await fetch(`${API}/api/consumption/items/list`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center }),
    });
    const d = await r.json(); setItems(d.items || []);
    // Load active price-update access windows for this centre
    try {
      const ar = await fetch(`${API}/api/consumption/price-access/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, status: "active" }),
      });
      const ad = await ar.json();
      setPriceAccessList(ad.rows || []);
    } catch { /* silently ignore */ }
  }, [session.token, center]);
  useEffect(() => { load(); }, [load]);

  // Access lookup — does the current session have permission to edit price for this item?
  const canEditPrice = (it) => {
    if (isSuperAdmin || session?.is_admin) return true;
    if (!priceAccessList || priceAccessList.length === 0) return false;
    return priceAccessList.some(a =>
      a.status === "active" &&
      (a.scope === "all"
        || (a.scope === "category" && (a.category || "").toLowerCase() === (it.category || "").toLowerCase())
        || (a.scope === "item" && (a.item_codes || []).includes(it.item_code)))
    );
  };
  const activePriceWindow = priceAccessList.find(a => a.status === "active");

  // Filter + sort state
  const [catFilter, setCatFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | archived
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const distinctVals = (key) => Array.from(new Set(items.map(i => i[key]).filter(Boolean))).sort();
  const cats = useMemo(() => distinctVals("category"), [items]);
  const vends = useMemo(() => distinctVals("vendor"), [items]);
  const unts = useMemo(() => distinctVals("unit"), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter(it => {
      if (statusFilter === "active" && it.active === false) return false;
      if (statusFilter === "archived" && it.active !== false) return false;
      if (statusFilter === "all" && !showInactive && it.active === false) return false;
      if (catFilter && String(it.category || "").toLowerCase() !== catFilter.toLowerCase()) return false;
      if (vendorFilter && String(it.vendor || "").toLowerCase() !== vendorFilter.toLowerCase()) return false;
      if (unitFilter && String(it.unit || "").toLowerCase() !== unitFilter.toLowerCase()) return false;
      if (!q) return true;
      return (it.item_code || "").toLowerCase().includes(q)
          || (it.item_name || "").toLowerCase().includes(q)
          || (it.category || "").toLowerCase().includes(q)
          || (it.vendor || "").toLowerCase().includes(q);
    });
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        const av = a[sortKey]; const bv = b[sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
      });
    }
    return list;
  }, [items, search, showInactive, catFilter, vendorFilter, unitFilter, statusFilter, sortKey, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = useMemo(() =>
    filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };
  const sortArrow = (key) => sortKey !== key ? " ↕" : sortDir === "asc" ? " ▲" : " ▼";

  const openDelete = async (it) => {
    try {
      const r = await fetch(`${API}/api/consumption/items/usage`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_code: it.item_code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Usage check failed");
      setDeleteDlg({ item: it, usage: d });
    } catch (e) { toast.error(e.message); }
  };

  const doDelete = async () => {
    if (!deleteDlg) return;
    setDeleting(true);
    try {
      const url = deleteDlg.usage.can_hard_delete
        ? `${API}/api/consumption/items/hard-delete`
        : `${API}/api/consumption/items/archive`;
      const r = await fetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_code: deleteDlg.item.item_code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || "Delete failed");
      toast.success(deleteDlg.usage.can_hard_delete ? "Item permanently deleted" : "Item archived");
      setDeleteDlg(null); await load();
    } catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const doArchive = async () => {
    if (!deleteDlg) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/api/consumption/items/archive`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_code: deleteDlg.item.item_code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Archive failed");
      toast.success("Item archived");
      setDeleteDlg(null); await load();
    } catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  // ── Bulk selection helpers ────────────────────────────────
  const clearSelection = () => setSelected({});
  const selectedCodes = useMemo(
    () => Object.keys(selected).filter(k => selected[k]),
    [selected]
  );
  const toggleSelectAllVisible = (checked) => {
    const next = { ...selected };
    pageRows.forEach(r => { next[r.item_code] = checked; });
    setSelected(next);
  };
  const selectAllFiltered = () => {
    const next = {};
    filtered.forEach(r => { next[r.item_code] = true; });
    setSelected(next);
  };
  const doBulkArchive = async (codes, source = "bulk-archive") => {
    if (!codes.length) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/items/bulk-archive`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_codes: codes }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Archive failed");
      toast.success(`Archived ${d.archived} item(s)`);
      setActionSummary({ source, selected: codes.length, archived: d.archived, deleted: 0, skipped: d.skipped, results: [] });
      clearSelection(); await load();
    } catch (e) { toast.error(e.message); } finally { setBulkBusy(false); }
  };
  const doBulkHardDelete = async (codes, reason, source = "bulk-hard-delete") => {
    if (!codes.length) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${API}/api/consumption/items/bulk-hard-delete`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_codes: codes, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || "Delete failed");
      toast.success(`Deleted ${d.deleted}, archived ${d.archived}`);
      setActionSummary({
        source, selected: codes.length,
        deleted: d.deleted, archived: d.archived, skipped: d.skipped, results: d.results || [],
      });
      clearSelection(); await load();
    } catch (e) { toast.error(e.message); } finally { setBulkBusy(false); }
  };
  const doRestore = async (it) => {
    if (!isSuperAdmin) return;
    try {
      const r = await fetch(`${API}/api/consumption/items/restore`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, item_code: it.item_code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Restore failed");
      toast.success("Item restored");
      await load();
    } catch (e) { toast.error(e.message); }
  };
  const openCategoryDialog = async (category) => {
    if (!category) { toast.error("Select a category first."); return; }
    try {
      const r = await fetch(`${API}/api/consumption/items/category-summary`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, category }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to load category");
      setCatDlg({ category, summary: d });
    } catch (e) { toast.error(e.message); }
  };
  const submitManagerPrice = async () => {
    if (!priceEditDlg?.proof) { toast.error("Photo proof is required."); return; }
    if (!priceEditDlg.confirm) { toast.error("Please confirm the declaration."); return; }
    if (!(priceEditDlg.newPrice > 0)) { toast.error("Enter a valid price."); return; }
    setPriceSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("token", session.token);
      fd.append("center", center);
      fd.append("item_code", priceEditDlg.item.item_code);
      fd.append("new_price", String(priceEditDlg.newPrice));
      if (priceEditDlg.vendor) fd.append("vendor", priceEditDlg.vendor);
      if (priceEditDlg.remarks) fd.append("remarks", priceEditDlg.remarks);
      fd.append("proof", priceEditDlg.proof);
      const r = await fetch(`${API}/api/consumption/price-updates/submit`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Submit failed");
      toast.success(
        d.update?.status === "auto_approved"
          ? `Price updated to ₹${d.update.new_price}`
          : "Submitted for approval"
      );
      setPriceEditDlg(null); await load();
    } catch (e) { toast.error(e.message); } finally { setPriceSubmitting(false); }
  };

  const downloadActionLog = () => {
    if (!actionSummary) return;
    const rows = [["Item Code", "Action", "Reason", "Consumption", "Invoices", "POs", "Recipes"]];    (actionSummary.results || []).forEach(r => rows.push([
      r.item_code, r.action, r.reason || "",
      r.counts?.consumption ?? 0, r.counts?.invoices ?? 0,
      r.counts?.purchase_orders ?? 0, r.counts?.recipes ?? 0,
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `item_action_log_${center}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/consumption/items/upsert`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, ...dlg }),
      });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Save failed");
      toast.success("Item saved"); setDlg(null); await load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  // ── Import Excel — preview + commit flow ───────────────────
  const [importDlg, setImportDlg] = useState(null); // { rows, summary }
  const [dupAction, setDupAction] = useState("skip");
  const [importFilter, setImportFilter] = useState("all");
  const [committing, setCommitting] = useState(false);

  const importExcel = async (file) => {
    const fd = new FormData();
    fd.append("token", session.token); fd.append("center", center); fd.append("file", file);
    const r = await fetch(`${API}/api/consumption/items/import-preview`, { method: "POST", body: fd });
    const d = await r.json();
    if (!r.ok) return toast.error(typeof d.detail === "string" ? d.detail : (d.detail?.message || "Preview failed"));
    setImportDlg({ rows: d.rows || [], summary: d.summary || {} });
    setImportFilter("all"); setDupAction("skip");
  };

  const commitImport = async () => {
    if (!importDlg) return;
    setCommitting(true);
    try {
      // Apply the dupAction to every duplicate row that doesn't have its own action
      const rows = importDlg.rows.map(r => ({
        ...r,
        action: r.action || (r.status === "duplicate" ? dupAction : undefined),
      }));
      const r = await fetch(`${API}/api/consumption/items/import-commit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, duplicate_action: dupAction, rows }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Import failed");
      toast.success(
        `Import complete — created ${d.summary.created}, updated ${d.summary.updated}` +
        (d.summary.prices_updated ? `, prices updated ${d.summary.prices_updated}` : "") +
        (d.summary.prices_kept_invoice_wap ? `, ${d.summary.prices_kept_invoice_wap} kept (invoice-derived WAP)` : "") +
        (d.summary.skipped ? `, skipped ${d.summary.skipped}` : "")
      );
      setImportDlg(null); await load();
    } catch (e) { toast.error(e.message); } finally { setCommitting(false); }
  };

  const downloadErrorReport = () => {
    if (!importDlg) return;
    const problem_rows = importDlg.rows.filter(r => r.status === "error" || r.status === "duplicate" || r.issue);
    const rows = [["Excel Row", "Item Code", "Item Name", "Status", "Issue / Warning"]];
    problem_rows.forEach(r => rows.push([r.row, r.item_code, r.item_name, r.status, r.issue || ""]));
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `import_errors_${center}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const filteredImportRows = importDlg
    ? importDlg.rows.filter(r => importFilter === "all" || r.status === importFilter)
    : [];

  return (<div className="space-y-3">
    <div className="flex flex-wrap gap-2 items-center">
      {isEditor && <Button size="sm" onClick={() => setDlg({ item_code: "", item_name: "", category: "General", unit: "kg", opening_qty: 0, opening_price: 0, expected_guest_pct: 50, expected_staff_pct: 50, target_pct: 30, reorder_level: 0, vendor: "", active: true })} data-testid="items-add"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>}
      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/api/consumption/items/template`, "_blank")} data-testid="items-template"><Download className="w-4 h-4 mr-1" /> Excel Template</Button>
      {isEditor && (<label><input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files?.[0] && importExcel(e.target.files[0])} data-testid="items-import" /><span className="inline-flex items-center px-3 py-1 border rounded text-sm cursor-pointer hover:bg-slate-100"><FileSpreadsheet className="w-4 h-4 mr-1" /> Import Excel</span></label>)}
      {isEditor && (
        <Button size="sm" variant="outline" onClick={() => openCategoryDialog(catFilter)}
                disabled={!catFilter} title={!catFilter ? "Pick a category first" : ""}
                data-testid="items-delete-by-category">
          <Trash2 className="w-3.5 h-3.5 mr-1 text-rose-600" /> Delete by Category
        </Button>
      )}
      {isSuperAdmin && (
        <Button size="sm" variant="outline"
                onClick={() => setBulkAllDlg({ count: filtered.length, reason: "", confirmText: "" })}
                data-testid="items-bulk-actions">
          <Trash2 className="w-3.5 h-3.5 mr-1 text-rose-600" /> Bulk Actions
        </Button>
      )}
      <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
             placeholder="Search code / name / category / vendor…"
             className="h-8 max-w-xs ml-2" data-testid="items-search" />
      <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}
              className="h-8 text-xs border rounded px-2" data-testid="items-cat-filter">
        <option value="">All categories</option>
        {cats.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={vendorFilter} onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }}
              className="h-8 text-xs border rounded px-2" data-testid="items-vendor-filter">
        <option value="">All vendors</option>
        {vends.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <select value={unitFilter} onChange={(e) => { setUnitFilter(e.target.value); setPage(1); }}
              className="h-8 text-xs border rounded px-2" data-testid="items-unit-filter">
        <option value="">All units</option>
        {unts.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
      <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-8 text-xs border rounded px-2" data-testid="items-status-filter">
        <option value="all">All</option>
        <option value="active">Active only</option>
        <option value="archived">Archived only</option>
      </select>
      <label className="text-xs flex items-center gap-1 ml-1">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
               data-testid="items-show-inactive" />
        Show archived
      </label>
      <div className="ml-auto text-xs text-slate-500" data-testid="items-count">
        Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
      </div>
    </div>

    {/* Read-only badge for Center Managers */}
    {isCenterManagerReadOnly && (
      <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs flex items-center gap-2"
           data-testid="items-readonly-badge">
        <Badge variant="outline" className="bg-slate-100">🔒 Read-only Access</Badge>
        <span className="text-slate-500">
          {activePriceWindow
            ? `Price-update access is active for ${activePriceWindow.scope === "all" ? "all items" : activePriceWindow.scope === "category" ? `category "${activePriceWindow.category}"` : "selected items"}${activePriceWindow.expires_at ? ` — expires ${(activePriceWindow.expires_at || "").slice(0, 16).replace("T", " ")}` : ""}.`
            : "Ask an Admin to open temporary price-update access when you need to edit rates."}
        </span>
      </div>
    )}

    {/* Bulk-action bar — only when at least one item is selected */}
    {selectedCodes.length > 0 && (
      <div className="bg-slate-100 border border-slate-300 rounded p-2 flex flex-wrap items-center gap-2"
           data-testid="items-bulk-bar">
        <span className="text-sm font-semibold text-slate-700">
          Selected: <span data-testid="items-selected-count">{selectedCodes.length}</span> item(s)
        </span>
        <Button size="sm" variant="outline" onClick={() => doBulkArchive(selectedCodes)}
                disabled={bulkBusy} data-testid="items-bulk-archive">
          {bulkBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          Archive Selected
        </Button>
        {isSuperAdmin && (
          <Button size="sm" variant="outline"
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  onClick={() => doBulkHardDelete(selectedCodes, "Bulk delete from selection")}
                  disabled={bulkBusy} data-testid="items-bulk-delete">
            {bulkBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            Delete Selected
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="items-bulk-clear">
          Clear Selection
        </Button>
      </div>
    )}

    {/* Post-action summary — shows counts + download log */}
    {actionSummary && (
      <div className="bg-emerald-50 border border-emerald-300 rounded p-2 text-xs flex flex-wrap items-center gap-3"
           data-testid="items-action-summary">
        <span className="font-semibold text-emerald-900">✓ Action completed:</span>
        <span>Selected: <strong>{actionSummary.selected}</strong></span>
        <span>Deleted: <strong>{actionSummary.deleted}</strong></span>
        <span>Archived: <strong>{actionSummary.archived}</strong></span>
        {actionSummary.skipped > 0 && <span>Skipped: <strong>{actionSummary.skipped}</strong></span>}
        {(actionSummary.results || []).length > 0 && (
          <Button size="sm" variant="ghost" onClick={downloadActionLog} data-testid="items-download-action-log">
            <Download className="w-3 h-3 mr-1" /> Download Action Log
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setActionSummary(null)}>✕</Button>
      </div>
    )}
    <Card><CardContent className="p-0 overflow-x-auto">
      <Table className="text-xs">
        <TableHeader><TableRow>
          {isEditor && (
            <TableHead className="w-8">
              <input
                type="checkbox"
                checked={pageRows.length > 0 && pageRows.every(r => selected[r.item_code])}
                onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                data-testid="items-select-all-visible"
                title="Select all visible on this page"
              />
            </TableHead>
          )}
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("item_code")} data-testid="items-sort-code">Code{sortArrow("item_code")}</TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("item_name")} data-testid="items-sort-name">Name{sortArrow("item_name")}</TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("category")} data-testid="items-sort-category">Category{sortArrow("category")}</TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("unit")} data-testid="items-sort-unit">Unit{sortArrow("unit")}</TableHead>
          <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("opening_qty")} data-testid="items-sort-opening">Opening{sortArrow("opening_qty")}</TableHead>
          <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("current_wap")} data-testid="items-sort-wap">Current WAP{sortArrow("current_wap")}</TableHead>
          <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("last_invoice_price")} data-testid="items-sort-lastinv">Last Invoice{sortArrow("last_invoice_price")}</TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("vendor")} data-testid="items-sort-vendor">Vendor{sortArrow("vendor")}</TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("active")} data-testid="items-sort-status">Status{sortArrow("active")}</TableHead>
          {isEditor && <TableHead>Actions</TableHead>}
        </TableRow></TableHeader>
        <TableBody>{pageRows.map((it) => (
          <TableRow key={it.item_code} className={it.active === false ? "opacity-50" : ""}>
            {isEditor && (
              <TableCell>
                <input
                  type="checkbox"
                  checked={!!selected[it.item_code]}
                  onChange={(e) => setSelected(s => ({ ...s, [it.item_code]: e.target.checked }))}
                  data-testid={`items-sel-${it.item_code}`}
                />
              </TableCell>
            )}
            <TableCell className="font-mono text-[10px]">{it.item_code}</TableCell>
            <TableCell className="font-semibold">{it.item_name}</TableCell>
            <TableCell>{it.category}</TableCell><TableCell>{it.unit}</TableCell>
            <TableCell className="text-right">{it.opening_qty}</TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <span>{it.current_wap}</span>
                {isCenterManagerReadOnly && canEditPrice(it) && (
                  <Button size="sm" variant="ghost" className="h-6 px-1 text-[#8B0000]"
                          onClick={() => setPriceEditDlg({ item: it, newPrice: it.current_wap, vendor: it.vendor || "", remarks: "", proof: null, confirm: false })}
                          data-testid={`items-mgr-edit-price-${it.item_code}`}
                          title="Edit Price (proof required)">
                    <Edit className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">{it.last_invoice_price || "—"}</TableCell>
            <TableCell>{it.vendor || "—"}</TableCell>
            <TableCell><Badge variant={it.active === false ? "outline" : "default"}>{it.active === false ? "Archived" : "Active"}</Badge></TableCell>
            {isEditor && <TableCell>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setDlg(it)} data-testid={`items-edit-${it.item_code}`}><Edit className="w-3.5 h-3.5" /></Button>
                {it.active === false && isSuperAdmin ? (
                  <Button size="sm" variant="ghost" className="text-emerald-700 hover:text-emerald-900"
                          onClick={() => doRestore(it)} data-testid={`items-restore-${it.item_code}`}
                          title="Restore archived item">↺</Button>
                ) : null}
                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-800" onClick={() => openDelete(it)} data-testid={`items-delete-${it.item_code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </TableCell>}
          </TableRow>
        ))}
        {pageRows.length === 0 && (
          <TableRow><TableCell colSpan={isEditor ? 11 : 9} className="text-center text-slate-500 py-6">
            {items.length === 0 ? "No items yet. Add or import via Excel." : "No items match your search."}
          </TableCell></TableRow>
        )}
        </TableBody>
      </Table>
    </CardContent></Card>

    {/* Pagination controls */}
    {totalPages > 1 && (
      <div className="flex items-center gap-2 justify-center" data-testid="items-pagination">
        <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(1)}>« First</Button>
        <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Prev</Button>
        <span className="text-xs px-2">Page {page} of {totalPages}</span>
        <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next ›</Button>
        <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last »</Button>
        <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                className="h-8 text-xs border rounded px-2 ml-2" data-testid="items-page-size">
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
          <option value={200}>200 / page</option>
          <option value={500}>500 / page</option>
        </select>
      </div>
    )}

    {/* Delete confirmation dialog */}
    {deleteDlg && (
      <Dialog open={true} onOpenChange={(o) => !o && setDeleteDlg(null)}>
        <DialogContent data-testid="items-delete-dialog">
          <DialogHeader>
            <DialogTitle>{deleteDlg.usage.can_hard_delete ? "Delete Item Permanently?" : "Cannot Delete — Archive Instead?"}</DialogTitle>
            <DialogDescription>
              {deleteDlg.usage.can_hard_delete
                ? <>This item has NO invoice, consumption or stock history and can be safely deleted.</>
                : <>This item is already used in <b>{deleteDlg.usage.invoice_lines}</b> invoice line(s) and <b>{deleteDlg.usage.consumption_entries}</b> consumption entry(ies). It cannot be permanently deleted. Archive instead to hide it from daily entry while preserving history.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs bg-slate-50 rounded p-2 space-y-0.5">
            <div><b>Item:</b> {deleteDlg.item.item_name}</div>
            <div><b>Code:</b> {deleteDlg.item.item_code}</div>
            <div><b>Invoice lines:</b> {deleteDlg.usage.invoice_lines}</div>
            <div><b>Consumption entries:</b> {deleteDlg.usage.consumption_entries}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDlg(null)} disabled={deleting} data-testid="items-delete-cancel">Cancel</Button>
            {deleteDlg.usage.can_hard_delete ? (
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} disabled={deleting} data-testid="items-delete-confirm">
                {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete Permanently
              </Button>
            ) : (
              <Button onClick={doArchive} disabled={deleting} data-testid="items-archive-confirm">
                {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Archive
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {/* ── Delete by Category dialog ─────────────────────────── */}
    {catDlg && (
      <Dialog open={true} onOpenChange={(o) => !o && setCatDlg(null)}>
        <DialogContent data-testid="items-category-dialog">
          <DialogHeader>
            <DialogTitle>Delete by Category — {catDlg.category}</DialogTitle>
            <DialogDescription>
              Review the counts below. Linked items will be archived instead of hard-deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <SummaryTile2 label="Total items" value={catDlg.summary.total_items} tone="slate" />
            <SummaryTile2 label="Active" value={catDlg.summary.active_items} tone="emerald" />
            <SummaryTile2 label="Archived" value={catDlg.summary.archived_items} tone="slate" />
            <SummaryTile2 label="Linked (cannot hard-delete)" value={catDlg.summary.linked_items} tone="amber" />
            <SummaryTile2 label="Eligible for Permanent Delete" value={catDlg.summary.eligible_permanent_delete} tone="rose" />
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setCatDlg(null)}>Cancel</Button>
            <Button variant="outline"
                    onClick={async () => {
                      const codes = [...catDlg.summary.eligible_codes, ...catDlg.summary.linked_codes];
                      await doBulkArchive(codes, "category-archive");
                      setCatDlg(null);
                    }}
                    disabled={bulkBusy || catDlg.summary.total_items === 0}
                    data-testid="items-cat-archive-all">
              Archive Entire Category
            </Button>
            {isSuperAdmin && (
              <Button className="bg-rose-700 hover:bg-rose-800"
                      onClick={async () => {
                        const codes = [...catDlg.summary.eligible_codes, ...catDlg.summary.linked_codes];
                        await doBulkHardDelete(codes, `Category-wise delete: ${catDlg.category}`, "category-delete");
                        setCatDlg(null);
                      }}
                      disabled={bulkBusy || catDlg.summary.eligible_permanent_delete === 0}
                      data-testid="items-cat-delete-eligible">
                {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete Eligible ({catDlg.summary.eligible_permanent_delete})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {/* ── Bulk Actions dialog (Super Admin only) ────────────── */}
    {bulkAllDlg && (
      <Dialog open={true} onOpenChange={(o) => !o && setBulkAllDlg(null)}>
        <DialogContent data-testid="items-bulk-all-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Actions — All Filtered Items</DialogTitle>
            <DialogDescription>
              Actions below apply to the currently-filtered <strong>{filtered.length}</strong> item(s).
              Linked items are automatically archived — permanent deletion only removes items with
              zero historical references.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <Button size="sm" variant="outline" className="w-full justify-start"
                    onClick={selectAllFiltered}
                    data-testid="items-bulk-select-all-filtered">
              Select All {filtered.length} Filtered Item(s)
            </Button>
            <Button size="sm" variant="outline" className="w-full justify-start"
                    onClick={async () => {
                      const codes = filtered.map(f => f.item_code);
                      await doBulkArchive(codes, "filtered-archive");
                      setBulkAllDlg(null);
                    }}
                    disabled={bulkBusy || filtered.length === 0}
                    data-testid="items-bulk-archive-filtered">
              Archive All Filtered
            </Button>
            <div className="border-t pt-2 mt-2 space-y-2">
              <p className="text-rose-800 font-semibold text-xs">
                ⚠️ You are about to permanently delete all eligible Item Master records.
                Linked items will not be deleted — they will be archived instead.
              </p>
              <div>
                <Label className="text-xs">Reason *</Label>
                <Input value={bulkAllDlg.reason}
                       onChange={(e) => setBulkAllDlg({ ...bulkAllDlg, reason: e.target.value })}
                       placeholder="Why are you deleting these items?"
                       data-testid="items-bulk-reason" />
              </div>
              <div>
                <Label className="text-xs">Type <span className="font-mono">DELETE</span> to confirm</Label>
                <Input value={bulkAllDlg.confirmText}
                       onChange={(e) => setBulkAllDlg({ ...bulkAllDlg, confirmText: e.target.value })}
                       data-testid="items-bulk-confirm-text" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAllDlg(null)}>Cancel</Button>
            <Button className="bg-rose-700 hover:bg-rose-800"
                    disabled={bulkBusy || bulkAllDlg.confirmText !== "DELETE" || !bulkAllDlg.reason.trim() || filtered.length === 0}
                    onClick={async () => {
                      const codes = filtered.map(f => f.item_code);
                      await doBulkHardDelete(codes, bulkAllDlg.reason.trim(), "filtered-hard-delete");
                      setBulkAllDlg(null);
                    }}
                    data-testid="items-bulk-delete-filtered">
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete All Eligible Filtered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {/* ── Manager Price-Update dialog (photo-mandatory) ─────── */}
    {priceEditDlg && (
      <Dialog open={true} onOpenChange={(o) => !o && setPriceEditDlg(null)}>
        <DialogContent data-testid="items-price-edit-dialog">
          <DialogHeader>
            <DialogTitle>Update Price — {priceEditDlg.item.item_name}</DialogTitle>
            <DialogDescription>
              Center: <strong>{center}</strong> · Item: <code>{priceEditDlg.item.item_code}</code>
              {activePriceWindow?.reason ? ` · Access reason: ${activePriceWindow.reason}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Existing Price</Label>
                <Input value={priceEditDlg.item.current_wap ?? 0} disabled className="bg-slate-50" /></div>
              <div><Label>New Price *</Label>
                <Input type="number" step="0.01" min="0" value={priceEditDlg.newPrice}
                       onChange={e => setPriceEditDlg({ ...priceEditDlg, newPrice: parseFloat(e.target.value) || 0 })}
                       data-testid="items-price-new" /></div>
            </div>
            {priceEditDlg.item.current_wap > 0 && priceEditDlg.newPrice > 0 && (
              <p className="text-[10px] text-slate-500">
                Difference: <span className="font-mono">₹{(priceEditDlg.newPrice - priceEditDlg.item.current_wap).toFixed(2)}</span>{" "}
                ({(((priceEditDlg.newPrice - priceEditDlg.item.current_wap) / priceEditDlg.item.current_wap) * 100).toFixed(2)}%)
              </p>
            )}
            <div><Label>Vendor</Label>
              <Input value={priceEditDlg.vendor}
                     onChange={e => setPriceEditDlg({ ...priceEditDlg, vendor: e.target.value })}
                     data-testid="items-price-vendor" /></div>
            <div><Label>Remarks</Label>
              <Input value={priceEditDlg.remarks}
                     onChange={e => setPriceEditDlg({ ...priceEditDlg, remarks: e.target.value })}
                     data-testid="items-price-remarks" /></div>
            <div>
              <Label>Photo / PDF Proof *</Label>
              <Input type="file" accept="image/jpeg,image/jpg,image/png,application/pdf"
                     onChange={e => setPriceEditDlg({ ...priceEditDlg, proof: e.target.files?.[0] || null })}
                     data-testid="items-price-proof" />
              {priceEditDlg.proof && (
                <p className="text-[10px] text-emerald-700 mt-1">
                  ✓ {priceEditDlg.proof.name} ({(priceEditDlg.proof.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <label className="flex items-start gap-2 text-[11px]">
              <input type="checkbox" checked={priceEditDlg.confirm}
                     onChange={e => setPriceEditDlg({ ...priceEditDlg, confirm: e.target.checked })}
                     data-testid="items-price-confirm" className="mt-0.5" />
              <span>I confirm that this image supports the updated price.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceEditDlg(null)}>Cancel</Button>
            <Button onClick={submitManagerPrice}
                    disabled={priceSubmitting || !priceEditDlg.proof || !priceEditDlg.confirm || !(priceEditDlg.newPrice > 0)}
                    data-testid="items-price-submit">
              {priceSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Submit Price Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}


    {dlg && (<Dialog open={true} onOpenChange={(o) => !o && setDlg(null)}>
      <DialogContent><DialogHeader><DialogTitle>{dlg.item_code ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Code *</Label><Input value={dlg.item_code} onChange={(e) => setDlg({ ...dlg, item_code: e.target.value })} data-testid="item-code" /></div>
          <div><Label>Name *</Label><Input value={dlg.item_name} onChange={(e) => setDlg({ ...dlg, item_name: e.target.value })} data-testid="item-name" /></div>
          <div><Label>Category</Label><Input value={dlg.category} onChange={(e) => setDlg({ ...dlg, category: e.target.value })} /></div>
          <div><Label>Unit</Label><Input value={dlg.unit} onChange={(e) => setDlg({ ...dlg, unit: e.target.value })} /></div>
          <div><Label>Opening Qty</Label><Input type="number" value={dlg.opening_qty} onChange={(e) => setDlg({ ...dlg, opening_qty: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>Opening Price</Label><Input type="number" value={dlg.opening_price} onChange={(e) => setDlg({ ...dlg, opening_price: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>Expected Guest %</Label><Input type="number" value={dlg.expected_guest_pct} onChange={(e) => setDlg({ ...dlg, expected_guest_pct: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>Expected Staff %</Label><Input type="number" value={dlg.expected_staff_pct} onChange={(e) => setDlg({ ...dlg, expected_staff_pct: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>Target %</Label><Input type="number" value={dlg.target_pct} onChange={(e) => setDlg({ ...dlg, target_pct: parseFloat(e.target.value) || 0 })} /></div>
          <div><Label>Vendor</Label><Input value={dlg.vendor || ""} onChange={(e) => setDlg({ ...dlg, vendor: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="item-save">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>)}

    {/* ── Excel Import Preview / Commit ─────────────────────── */}
    {importDlg && (
      <Dialog open={true} onOpenChange={(o) => !o && setImportDlg(null)}>
        <DialogContent className="max-w-6xl" data-testid="items-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Preview — {center}</DialogTitle>
            <DialogDescription>
              Review each row before committing. Valid rows are ready to import. Warning rows will
              also import (blank category → &quot;Uncategorized&quot;, auto-generated codes,
              parsed pack prices). Error rows are blocked. Choose an action for duplicates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              <SummaryTile2 label="Total" value={importDlg.summary.total_rows} tone="slate" />
              <SummaryTile2 label="Ready" value={importDlg.summary.ready} tone="emerald" />
              <SummaryTile2 label="Warning" value={importDlg.summary.warning} tone="amber" />
              <SummaryTile2 label="Duplicate" value={importDlg.summary.duplicate} tone="sky" />
              <SummaryTile2 label="Price Pending" value={importDlg.summary.price_pending} tone="indigo" />
              <SummaryTile2 label="Error" value={importDlg.summary.error} tone="rose" />
              <SummaryTile2 label="Existing" value={importDlg.summary.existing_items} tone="sky" />
              <SummaryTile2 label="New" value={importDlg.summary.new_items} tone="emerald" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs">Show</Label>
              {["all", "ready", "warning", "duplicate", "price_pending", "error"].map(k => (
                <Button key={k} size="sm" variant={importFilter === k ? "default" : "outline"}
                        onClick={() => setImportFilter(k)}
                        className="h-7 text-[11px] capitalize"
                        data-testid={`items-import-filter-${k}`}>
                  {k.replace("_", " ")}
                </Button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs">On duplicate</Label>
                <select value={dupAction} onChange={(e) => setDupAction(e.target.value)}
                        className="h-7 text-xs border rounded px-2" data-testid="items-import-dup-action">
                  <option value="skip">Skip existing</option>
                  <option value="update">Update existing</option>
                  <option value="create_new">Create new (regenerate code)</option>
                </select>
                <Button size="sm" variant="outline" onClick={downloadErrorReport} data-testid="items-import-error-report">
                  <Download className="w-3.5 h-3.5 mr-1" /> Error Report
                </Button>
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto border rounded">
              <Table className="text-[11px]">
                <TableHeader className="sticky top-0 bg-slate-100">
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Price (raw)</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Op. Value</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Issue / Warning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredImportRows.map((r, idx) => (
                    <TableRow key={idx} data-testid={`items-import-row-${r.row}`}
                              className={
                                r.status === "error" ? "bg-rose-50" :
                                r.status === "duplicate" ? "bg-sky-50" :
                                r.status === "price_pending" ? "bg-indigo-50" :
                                r.status === "warning" ? "bg-amber-50" : ""
                              }>
                      <TableCell>{r.row}</TableCell>
                      <TableCell className="font-mono text-[10px]">{r.item_code}</TableCell>
                      <TableCell className="font-medium">{r.item_name}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.unit}</TableCell>
                      <TableCell className="text-right">{r.opening_qty}</TableCell>
                      <TableCell className="text-slate-600">{r.original_price_text || "—"}</TableCell>
                      <TableCell className="text-right">{r.parsed_unit_price}</TableCell>
                      <TableCell className="text-right">{r.opening_value}</TableCell>
                      <TableCell>{r.vendor || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] capitalize">{(r.status || "").replace("_", " ")}</Badge></TableCell>
                      <TableCell className="text-slate-600 max-w-[240px]">{r.issue || ""}</TableCell>
                    </TableRow>
                  ))}
                  {filteredImportRows.length === 0 && (
                    <TableRow><TableCell colSpan={12} className="text-center text-slate-500 py-6">
                      No rows match the current filter.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDlg(null)} data-testid="items-import-cancel">Cancel</Button>
            <Button onClick={commitImport} disabled={committing || importDlg.summary.total_rows === 0}
                    data-testid="items-import-commit">
              {committing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Import {importDlg.summary.ready + importDlg.summary.warning + importDlg.summary.price_pending} valid rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
  </div>);
}

function SummaryTile2({ label, value, tone = "slate" }) {
  const t = ({
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  })[tone];
  return (
    <div className={`border rounded p-2 text-center ${t}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-lg font-bold mt-0.5">{value || 0}</p>
    </div>
  );
}

// ═════════════════════════ REPORTS ═════════════════════════
function ReportsTab({ session, center, fromDate, toDate }) {
  const isAdmin = Boolean(session?.is_super_admin || session?.is_admin || session?.is_accounts);
  const [kind, setKind] = useState("daily");
  const [itemCode, setItemCode] = useState("");
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [scope, setScope] = useState("consumed");  // "consumed" (default) | "all"
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!center) return;
    (async () => {
      const r = await fetch(`${API}/api/consumption/items/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center }),
      });
      const d = await r.json();
      setItems(d.items || []);
    })();
  }, [session.token, center]);

  const categories = useMemo(
    () => Array.from(new Set(items.map(i => i.category).filter(Boolean))).sort(),
    [items],
  );

  const generate = async () => {
    if (!center) { toast.error("Pick a center"); return; }
    if (kind === "item_wise" && !itemCode) { toast.error("Pick an item"); return; }
    setLoading(true); setPreview(null);
    try {
      const r = await fetch(`${API}/api/consumption/reports/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center, from_date: fromDate, to_date: toDate,
          kind, item_code: itemCode || undefined, category: category || undefined,
          include_all: scope === "all",
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Preview failed");
      setPreview(d);
      toast.success(`Loaded ${d.row_count} row${d.row_count === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const download = async (format) => {
    setBusy(format);
    try {
      const r = await fetch(`${API}/api/consumption/reports/export`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center,
          from_date: fromDate, to_date: toDate,
          kind, format,
          category: category || undefined,
          item_code: itemCode || undefined,
          include_all: scope === "all",
          search: search || undefined,
        }),
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `consumption_${kind}_${center}_${fromDate}_${toDate}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); } finally { setBusy(null); }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    if (!q) return preview.rows;
    return preview.rows.filter(r =>
      (r.item_name || "").toLowerCase().includes(q) ||
      (r.item_code || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q)
    );
  }, [preview, search]);

  const sym = preview?.currency?.symbol || "₹";
  const fmtNum = (v) => (v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  const fmtMoney = (v) => `${sym}${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  // Column definitions per report kind
  const dailyCols = [
    { k: "date", label: "Date" },
    { k: "item_name", label: "Item" },
    { k: "opening_qty", label: "Opening", num: true },
    { k: "purchased_qty", label: "Purchased", num: true },
    { k: "available_qty", label: "Available", num: true },
    { k: "staff_qty", label: "Staff", num: true },
    { k: "guest_qty", label: "Guest", num: true },
    { k: "total_consumption_qty", label: "Total Cons.", num: true },
    { k: "closing_qty", label: "Closing", num: true },
    { k: "rate", label: "Rate", money: true },
    { k: "consumption_cost", label: "Cons. Cost", money: true },
    { k: "total_sales", label: "Total Sales", money: true, periodConst: true },
    { k: "pct_of_food_cost", label: "% Food Cost", pct: true },
    { k: "pct_of_sales", label: "% Sales", pct: true },
    { k: "remarks", label: "Remarks" },
  ];
  const monthlyCols = [
    { k: "item_name", label: "Item" },
    { k: "opening_qty", label: "Opening", num: true },
    { k: "purchased_qty", label: "Purchased", num: true },
    { k: "available_qty", label: "Available", num: true },
    { k: "staff_qty", label: "Staff", num: true },
    { k: "guest_qty", label: "Guest", num: true },
    { k: "total_consumption_qty", label: "Total Cons.", num: true },
    { k: "closing_qty", label: "Closing", num: true },
    { k: "avg_rate", label: "Avg Rate", money: true },
    { k: "staff_cost", label: "Staff Cost", money: true },
    { k: "guest_cost", label: "Guest Cost", money: true },
    { k: "consumption_cost", label: "Cons. Cost", money: true },
    { k: "total_sales", label: "Total Sales", money: true, periodConst: true },
    { k: "pct_of_food_cost", label: "% Food Cost", pct: true },
    { k: "pct_of_sales", label: "% Sales", pct: true },
    { k: "closing_stock_value", label: "Closing Value", money: true },
  ];
  const cols = kind === "monthly" ? monthlyCols : dailyCols;

  return (<div className="space-y-3">
    {/* ── Filter form ───────────────────────────────────────── */}
    <Card><CardContent className="p-4 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-xs">Report Type</Label>
          <select value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); }}
                  className="h-9 w-full text-sm border rounded px-2" data-testid="reports-kind">
            <option value="daily">Daily Consumption</option>
            <option value="monthly">Monthly Consumption</option>
            <option value="item_wise">Item-wise Consumption</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Center</Label>
          <Input value={center || ""} disabled className="h-9" />
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input value={fromDate || ""} disabled className="h-9" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input value={toDate || ""} disabled className="h-9" />
        </div>
        <div>
          <Label className="text-xs">Category (opt.)</Label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="h-9 w-full text-sm border rounded px-2" data-testid="reports-category">
            <option value="">All</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Scope</Label>
          {isAdmin ? (
            <select value={scope} onChange={(e) => { setScope(e.target.value); setPreview(null); }}
                    className="h-9 w-full text-sm border rounded px-2" data-testid="reports-scope">
              <option value="consumed">Consumed Items Only</option>
              <option value="all">All Items</option>
            </select>
          ) : (
            <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-xs text-slate-700"
                 data-testid="reports-scope-locked" title="Only consumed items are included in this report">
              Consumed Items Only
            </div>
          )}
        </div>
        {kind === "item_wise" && (
          <div className="md:col-span-2">
            <Label className="text-xs">Item</Label>
            <select value={itemCode} onChange={(e) => setItemCode(e.target.value)}
                    className="h-9 w-full text-sm border rounded px-2" data-testid="reports-item">
              <option value="">-- Select --</option>
              {items.map(i => <option key={i.item_code} value={i.item_code}>{i.item_name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={generate} disabled={loading} data-testid="reports-generate">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />} Generate Report
        </Button>
      </div>
    </CardContent></Card>

    {/* ── On-screen preview grid ─────────────────────────────── */}
    {preview && (
      <Card data-testid="reports-preview-card"><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">
            {kind === "daily" ? "Daily Consumption Report" : kind === "monthly" ? "Monthly Consumption Report" : "Item-wise Report"}
            {" · "}{center} · {fromDate} → {toDate}
          </p>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search item / code / category…"
                 className="h-8 max-w-xs" data-testid="reports-search" />
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => download("excel")} disabled={busy}
                    data-testid="reports-download-excel">
              {busy === "excel" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Download Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.print()}
                    data-testid="reports-print">
              <FileText className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        </div>

        {/* ── Period sales & food-cost summary tiles ─────────────────── */}
        {preview.summary && (() => {
          const tileMap = [
            { label: "Period Sales", value: fmtMoney(preview.summary.period_sales), color: "bg-sky-50 border-sky-200 text-sky-900" },
            { label: "Total Food Cost", value: fmtMoney(preview.summary.total_food_cost), color: "bg-amber-50 border-amber-200 text-amber-900" },
            { label: "Guest Cost", value: fmtMoney(preview.summary.guest_cost), color: "bg-rose-50 border-rose-200 text-rose-900" },
            { label: "Staff Cost", value: fmtMoney(preview.summary.staff_cost), color: "bg-indigo-50 border-indigo-200 text-indigo-900" },
            { label: "Food Cost %", value: `${(preview.summary.food_cost_pct || 0).toFixed(2)}%`, color: "bg-emerald-50 border-emerald-200 text-emerald-900" },
            { label: "Items Consumed", value: String(preview.summary.unique_items || 0), color: "bg-slate-50 border-slate-200 text-slate-900" },
          ];
          return (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="reports-summary-tiles">
              {tileMap.map(t => (
                <div key={t.label} className={`border rounded p-2 ${t.color}`}>
                  <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{t.label}</div>
                  <div className="text-sm font-bold font-mono mt-0.5">{t.value}</div>
                </div>
              ))}
            </div>
          );
        })()}


        <div className="overflow-x-auto border rounded max-h-[560px] overflow-y-auto">
          <Table className="text-xs">
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>{cols.map(c => (
                <TableHead key={c.k} className={c.num || c.money || c.pct ? "text-right" : ""}>{c.label}</TableHead>
              ))}</TableRow>
            </TableHeader>
            <TableBody>
              {/* ── FIXED TOTAL ROW (always ALL items in period, never affected by search) ── */}
              {preview.totals && (
                <TableRow className="bg-amber-100 font-bold border-b-2 border-amber-400 sticky z-[5]"
                          style={{ top: "36px" }}
                          data-testid="reports-totals">
                  {cols.map((c, idx) => {
                    if (idx === 0) return <TableCell key={c.k} className="font-bold text-amber-900">TOTAL (All Items)</TableCell>;
                    const units = preview.totals.unique_units || [];
                    const multiUnit = units.length > 1;
                    // Money-based TOTAL fields
                    const moneyMap = {
                      consumption_cost: "total_consumption_cost",
                      staff_cost: "total_staff_cost",
                      guest_cost: "total_guest_cost",
                      closing_stock_value: "total_closing_stock_value",
                    };
                    // Value-based totals (Opening / Purchased / Available shown as VALUE in TOTAL row per spec §3)
                    if (c.k === "opening_qty")   return <TableCell key={c.k} className="text-right font-mono">{fmtMoney(preview.totals.total_opening_value || 0)}</TableCell>;
                    if (c.k === "purchased_qty") return <TableCell key={c.k} className="text-right font-mono">{fmtMoney(preview.totals.total_purchased_value || 0)}</TableCell>;
                    if (c.k === "available_qty") return <TableCell key={c.k} className="text-right font-mono">{fmtMoney((preview.totals.total_opening_value || 0) + (preview.totals.total_purchased_value || 0))}</TableCell>;
                    if (c.k === "closing_qty")   return <TableCell key={c.k} className="text-right font-mono">{fmtMoney(preview.totals.total_closing_value || 0)}</TableCell>;
                    // Mixed-unit qty columns → "Multiple Units"
                    if (["staff_qty","guest_qty","total_consumption_qty"].includes(c.k)) {
                      const totalKeyMap = {
                        staff_qty: "total_staff_qty",
                        guest_qty: "total_guest_qty",
                        total_consumption_qty: "total_consumption_qty",
                      };
                      const v = preview.totals[totalKeyMap[c.k]];
                      return <TableCell key={c.k} className="text-right font-mono">
                        {multiUnit ? <span className="italic text-slate-500">Multiple Units</span> : fmtNum(v || 0)}
                      </TableCell>;
                    }
                    if (c.k === "rate" || c.k === "avg_rate") return <TableCell key={c.k} className="text-right text-slate-400">—</TableCell>;
                    if (c.k === "total_sales") return <TableCell key={c.k} className="text-right font-mono font-bold text-sky-900">{fmtMoney(preview.summary?.period_sales || 0)}</TableCell>;
                    if (c.k === "pct_of_food_cost") return <TableCell key={c.k} className="text-right font-mono">100.00%</TableCell>;
                    if (c.k === "pct_of_sales") {
                      return <TableCell key={c.k} className="text-right font-mono font-bold">
                        {(preview.summary?.food_cost_pct || 0).toFixed(2)}%
                      </TableCell>;
                    }
                    if (moneyMap[c.k]) {
                      return <TableCell key={c.k} className="text-right font-mono">{fmtMoney(preview.totals[moneyMap[c.k]] || 0)}</TableCell>;
                    }
                    return <TableCell key={c.k}></TableCell>;
                  })}
                </TableRow>
              )}
              {/* ── SELECTED ITEMS TOTAL row (in-table, only when search filters some) ── */}
              {search.trim() && filteredRows.length > 0 && filteredRows.length < (preview.rows?.length || 0) && (() => {
                const selCost = filteredRows.reduce((a, r) => a + Number(r.consumption_cost || 0), 0);
                const selGuest = filteredRows.reduce((a, r) => a + Number(r.guest_cost || 0), 0);
                const selStaff = filteredRows.reduce((a, r) => a + Number(r.staff_cost || 0), 0);
                const selClosing = filteredRows.reduce((a, r) => a + Number(r.closing_stock_value || 0), 0);
                const totalFood = preview.summary?.total_food_cost || 0;
                const sales = preview.summary?.period_sales || 0;
                const foodPct = totalFood > 0 ? (selCost / totalFood) * 100 : 0;
                const salesPct = sales > 0 ? (selCost / sales) * 100 : 0;
                return (
                  <TableRow className="bg-emerald-50 font-semibold border-b border-emerald-300"
                            data-testid="reports-selected-total-row">
                    {cols.map((c, idx) => {
                      if (idx === 0) return <TableCell key={c.k} className="text-emerald-900">SELECTED ({filteredRows.length} item{filteredRows.length > 1 ? "s" : ""})</TableCell>;
                      if (c.k === "consumption_cost") return <TableCell key={c.k} className="text-right font-mono text-emerald-900">{fmtMoney(selCost)}</TableCell>;
                      if (c.k === "guest_cost") return <TableCell key={c.k} className="text-right font-mono text-emerald-900">{fmtMoney(selGuest)}</TableCell>;
                      if (c.k === "staff_cost") return <TableCell key={c.k} className="text-right font-mono text-emerald-900">{fmtMoney(selStaff)}</TableCell>;
                      if (c.k === "closing_stock_value") return <TableCell key={c.k} className="text-right font-mono text-emerald-900">{fmtMoney(selClosing)}</TableCell>;
                      if (c.k === "total_sales") return <TableCell key={c.k} className="text-right font-mono text-sky-900">{fmtMoney(sales)}</TableCell>;
                      if (c.k === "pct_of_food_cost") return <TableCell key={c.k} className="text-right font-mono">{foodPct.toFixed(2)}%</TableCell>;
                      if (c.k === "pct_of_sales") return <TableCell key={c.k} className="text-right font-mono">{salesPct.toFixed(2)}%</TableCell>;
                      return <TableCell key={c.k}></TableCell>;
                    })}
                  </TableRow>
                );
              })()}
              {/* ── Filtered rows ── */}
              {filteredRows.map((r, i) => (
                <TableRow key={i} data-testid={`reports-row-${i}`}>
                  {cols.map(c => (
                    <TableCell key={c.k} className={c.num || c.money || c.pct ? "text-right font-mono" : ""}>
                      {c.money
                        ? fmtMoney(r[c.k])
                        : c.pct
                          ? `${Number(r[c.k] || 0).toFixed(2)}%`
                          : c.num
                            ? fmtNum(r[c.k])
                            : (r[c.k] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {filteredRows.length === 0 && (
                <TableRow><TableCell colSpan={cols.length} className="text-center text-slate-500 py-6">
                  {search.trim() ? "No rows match your search — TOTAL row above still reflects the full period." : "No rows for this range."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <p className="text-[10px] text-slate-500">
          Showing {filteredRows.length} of {preview.row_count} rows. TOTAL row is always for the full period. Excel export mirrors what you see (including current search + Selected Items summary).
        </p>
      </CardContent></Card>
    )}

    {!preview && !loading && (
      <Card><CardContent className="p-6 text-center text-slate-500 text-sm">
        Pick a report type &amp; filters, then click <b>Generate Report</b> to preview on screen. Download Excel / Print buttons appear after preview loads.
      </CardContent></Card>
    )}
  </div>);
}
